/**
 * EspejosLED — Product Configurator
 * Vanilla JS, ES6, no dependencies.
 *
 * Entry point: reads [data-configurator] attribute on the <form> element,
 * parses the embedded JSON config, and initialises the configurator UI.
 *
 * Config JSON shape (see metafield schema):
 * {
 *   template_id: number,
 *   type: "rectangular" | "circular" | "predefined_sizes" | "simple_with_accessories",
 *   base_price: number,
 *   groups: [{ label: string, fields: [{ key, label, type, affects_price, options }] }],
 *   price_table: { "WxH": number },
 *   transport_table: { "WxH": number }
 * }
 */

(function () {
  'use strict';

  var STICKY_BUTTON_SELECTOR = 'sticky-atc-bar button[name="add"]';

  // ---------------------------------------------------------------------------
  // Boot
  // ---------------------------------------------------------------------------
  function boot() {
    // Remove any orphaned page-loader overlay left in the DOM by a previous
    // version of this script (bfcache can restore it as visible).
    var orphan = document.getElementById('cfg-page-loader-overlay');
    if (orphan) orphan.remove();
    document.querySelectorAll('.cfg-page-loader').forEach(function (el) { el.remove(); });

    var form = document.querySelector('.configurator-form');
    if (!form) return;

    var dataEl = document.getElementById('configurator-data');
    if (!dataEl) return;

    var config;
    try {
      config = JSON.parse(dataEl.textContent);
    } catch (e) {
      console.error('[Configurator] Failed to parse config JSON:', e);
      return;
    }

    // Load optional fields-info (image + text per field key for ⓘ popovers)
    var fieldsInfo = {};
    var infoEl = document.getElementById('configurator-fields-info');
    if (infoEl) {
      try {
        fieldsInfo = JSON.parse(infoEl.textContent) || {};
      } catch (e) {
        console.warn('[Configurator] Could not parse configurator-fields-info:', e);
      }
    }

    expandSizeSteps(config);
    initConfigurator(form, config, fieldsInfo);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  // ---------------------------------------------------------------------------
  // Medidas en pasos de 5 cm
  //
  // Las tarifas de fábrica llegan en bandas de 10 cm, pero los espejos se
  // fabrican al centímetro. Insertamos el punto medio entre dos bandas
  // consecutivas (40, 45, 50…) y cobramos la banda superior: un 45x65 cuesta
  // lo mismo que un 50x70. Solo se parte un hueco de exactamente 10 cm, para no
  // ofrecer medidas en los saltos que la tarifa no cubre (p. ej. 100 → 120).
  // Las tablas del metafield siguen en bandas de 10; lookupSizeValue() redondea.
  //
  // Ojo: boot() se ejecuta mas arriba en este mismo fichero, asi que aqui solo
  // valen declaraciones de funcion (se hoistean). Una constante a nivel de
  // modulo llegaria sin asignar y tumbaria el render entero.
  // ---------------------------------------------------------------------------
  function sizeStepKeys() {
    return ['ancho', 'alto', 'ancho1', 'alto1', 'diametro'];
  }

  function expandSizeSteps(config) {
    if (!config || config.type === 'predefined_sizes') return;
    var keys = sizeStepKeys();
    try {
      (config.groups || []).forEach(function (group) {
        (group.fields || []).forEach(function (field) {
          if (keys.indexOf(field.key) === -1) return;
          field.options = withMidpoints(field.options);
        });
      });
    } catch (e) {
      // Nunca dejar la ficha sin configurador por culpa de los pasos de 5 cm.
      console.warn('[Configurator] No se pudieron expandir las medidas:', e);
    }
  }

  // Medidas fijas (menos de 4 bandas, p. ej. Mallorca Ø60/Ø80) se dejan igual.
  function withMidpoints(options) {
    if (!options || options.length < 4) return options;
    var values = options.map(function (opt) { return parseFloat(opt.value); });
    var hasText = values.some(function (value) { return isNaN(value); });
    if (hasText) return options;

    var expanded = [options[0]];
    for (var i = 1; i < options.length; i += 1) {
      if (values[i] - values[i - 1] === 10) {
        var mid = values[i - 1] + 5;
        expanded.push({ label: mid + ' cm', value: String(mid), default: false });
      }
      expanded.push(options[i]);
    }
    return expanded;
  }

  // Precio de una medida en una tabla en bandas de 10: exacta, o la banda
  // inmediatamente superior de cada eje.
  function lookupSizeValue(table, sizeKey) {
    if (!table || !sizeKey) return 0;
    if (table[sizeKey] !== undefined) return parseFloat(table[sizeKey]) || 0;
    var band = nextSizeBand(table, sizeKey);
    return band === null ? 0 : parseFloat(table[band]) || 0;
  }

  function nextSizeBand(table, sizeKey) {
    var keys = Object.keys(table);
    if (!keys.length) return null;

    var requested = String(sizeKey).split('x');
    if (requested.length === 2) {
      var widths = [];
      var heights = [];
      keys.forEach(function (key) {
        var parts = key.split('x');
        if (parts.length !== 2) return;
        widths.push(parseFloat(parts[0]));
        heights.push(parseFloat(parts[1]));
      });
      var band = ceilBand(widths, parseFloat(requested[0])) + 'x' + ceilBand(heights, parseFloat(requested[1]));
      return table[band] !== undefined ? band : null;
    }

    var diameters = keys.map(function (key) { return parseFloat(key); }).filter(function (value) {
      return !isNaN(value);
    });
    var single = String(ceilBand(diameters, parseFloat(sizeKey)));
    return table[single] !== undefined ? single : null;
  }

  // Los accesorios se tarifan por el tamaño, unos por rango y otros por lista de
  // valores exactos (Ø60, Ø70…). Una medida intermedia no casaría con ninguna
  // lista y el accesorio saldría gratis, así que evaluamos con la banda superior:
  // un Ø65 paga los accesorios del Ø70. Las condiciones de visibilidad siguen
  // usando la medida real.
  function applyTariffBands(state, config) {
    if (!config || config.type === 'predefined_sizes') return;
    var table = config.price_table || {};
    var keys = Object.keys(table);
    if (!keys.length) return;

    function band(values, key) {
      if (state[key] === undefined || state[key] === '') return;
      var banded = ceilBand(values, parseFloat(state[key]));
      if (!isNaN(banded)) state[key] = banded;
    }

    if (keys[0].indexOf('x') !== -1) {
      var widths = [];
      var heights = [];
      keys.forEach(function (key) {
        var parts = key.split('x');
        if (parts.length !== 2) return;
        widths.push(parseFloat(parts[0]));
        heights.push(parseFloat(parts[1]));
      });
      band(widths, 'ancho');
      band(widths, 'ancho1');
      band(heights, 'alto');
      band(heights, 'alto1');
      return;
    }

    var diameters = keys.map(function (key) { return parseFloat(key); }).filter(function (value) {
      return !isNaN(value);
    });
    band(diameters, 'diametro');
    band(diameters, 'ancho');
    band(diameters, 'alto');
  }

  function ceilBand(values, value) {
    var sorted = values.filter(function (candidate) { return !isNaN(candidate); })
      .sort(function (a, b) { return a - b; });
    if (!sorted.length || isNaN(value)) return value;
    for (var i = 0; i < sorted.length; i += 1) {
      if (sorted[i] >= value) return sorted[i];
    }
    return sorted[sorted.length - 1];
  }

  // ---------------------------------------------------------------------------
  // Initialisation
  // ---------------------------------------------------------------------------
  function initConfigurator(form, config, fieldsInfo) {
    fieldsInfo = fieldsInfo || {};
    var wrapper = form.querySelector('.configurator-wrapper');
    if (!wrapper) return;
    var feedback = form.querySelector('.configurator-feedback');

    // Fichas con varias variantes (los "Listos para enviar"): las tablas del
    // metafield suman sobre la primera variante. Si la URL trae otra (?variant=),
    // se recarga sin ella para no cobrar la medida dos veces.
    if (config.base_variant_id && String(form.getAttribute('data-variant-id')) !== String(config.base_variant_id)) {
      var pageUrl = new URL(window.location.href);
      if (pageUrl.searchParams.has('variant')) {
        pageUrl.searchParams.delete('variant');
        window.location.replace(pageUrl.toString());
        return;
      }
    }

    form.dataset.configuratorSubmitting = 'false';

    window.addEventListener('pageshow', function () {
      form.dataset.configuratorSubmitting = 'false';
      setButtonLoading(findNativeAddBtn(), false);
      hideFeedback(feedback);
    });

    // Read base price from Shopify variant (native), fallback to metafield
    var basePrice      = parseFloat(form.getAttribute('data-price'))            || parseFloat(config.base_price) || 0;
    var compareAtPrice = parseFloat(form.getAttribute('data-compare-at-price')) || 0;

    // Fixed discount % from Shopify native prices (doesn't change with size)
    var fixedDiscountPct = 0;
    if (compareAtPrice > basePrice && compareAtPrice > 0) {
      fixedDiscountPct = Math.round((compareAtPrice - basePrice) / compareAtPrice * 100);
    }

    // Shared state: last computed total in cents (used by handleAddToCart)
    var lastTotalCents = 0;

    // Repinta el resumen "Tu espejo" (lo crea initSummary mas abajo).
    var refreshSummary = null;

    // Hide accelerated-checkout buttons ("Comprar ahora" / Shop Pay / etc.).
    // These bypass the form submit and cannot be intercepted; they must be hidden.
    document.querySelectorAll(
      '.shopify-payment-button, [data-shopify="payment-button"], ' +
      '.dynamic-checkout__content, dynamic-checkout'
    ).forEach(function (el) { el.style.display = 'none'; });

    // Intercept the native Shopify product form submit at window level (capture phase).
    // Modiva uses <product-form is="product-form"> custom element.
    var nativeForm = document.querySelector('product-form form');
    if (nativeForm) {
      window.addEventListener('submit', function (e) {
        if (e.target !== nativeForm) return; // only intercept the native product form
        e.preventDefault();
        e.stopImmediatePropagation();
        var nativeBtn = nativeForm.querySelector('button[name="add"]');
        handleAddToCart(form, config, lastTotalCents, nativeBtn);
      }, true);
    }

    // ---------------------------------------------------------------------------
    // Sticky bar interception — two complementary layers:
    //
    // Layer 1 (primary): Override handleAddToCartClick() on the sticky-add-to-cart
    //   custom element. This is a PUBLIC method that Horizon calls when the sticky
    //   button is clicked. Overriding it on the element instance is stable across
    //   morph() calls because morph only replaces innerHTML, not the element itself.
    //
    // Layer 2 (fallback): pointerdown capture on the sticky bar container.
    //   Unlike 'click', pointerdown fires on a disabled button's parent, so it
    //   catches the case where the button is momentarily disabled.
    // ---------------------------------------------------------------------------
    // Modiva uses <sticky-atc-bar> custom element (no public handleAddToCartClick).
    // Use pointerdown capture on the container to intercept ATC clicks.
    function overrideStickyBar() {
      var stickyEl = document.querySelector('sticky-atc-bar');
      if (!stickyEl || stickyEl._cfgOverridden) return;
      stickyEl._cfgOverridden = true;

      // Hasta que el cliente toca el configurador el boton dice "Elegir medida"
      // y lleva a el: antes añadia la medida minima sin haber configurado nada.
      if (!isConfiguratorTouched(form)) setStickyGate(form, true);

      stickyEl.addEventListener('pointerdown', function (e) {
        var btn = e.target.closest('button[name="add"]');
        if (!btn) return;
        e.preventDefault();
        e.stopImmediatePropagation();
        if (!isConfiguratorTouched(form)) return; // lo resuelve el clic
        handleAddToCart(form, config, lastTotalCents, btn);
      }, true);

      // El clic solo llega cuando pointerdown no ha dejado el boton en
      // "Añadiendo…": antes de configurar, o al pulsarlo con el teclado. En
      // ningun caso debe enviarse el formulario de la barra, que añadiria la
      // variante base sin configurar.
      stickyEl.addEventListener('click', function (e) {
        var btn = e.target.closest('button[name="add"]');
        if (!btn) return;
        e.preventDefault();
        e.stopImmediatePropagation();
        if (!isConfiguratorTouched(form)) {
          scrollToConfigurator(form);
          return;
        }
        handleAddToCart(form, config, lastTotalCents, btn);
      }, true);
    }

    if (document.querySelector('sticky-atc-bar')) {
      overrideStickyBar();
    } else {
      var stickyWatcher = new MutationObserver(function () {
        if (document.querySelector('sticky-atc-bar')) {
          overrideStickyBar();
          stickyWatcher.disconnect();
        }
      });
      stickyWatcher.observe(document.body, { childList: true, subtree: true });
    }

    // Render all groups + fields
    renderGroups(wrapper, config, fieldsInfo);
    initMeasureSliders(form, config);
    initRalControls(wrapper, fieldsInfo);

    // Bind change events on selects and checkboxes
    wrapper.querySelectorAll('select[data-config-key], input[data-config-key]').forEach(function (el) {
      el.addEventListener('change', function () {
        applyConditions(wrapper, config);
        applyVisibilityRules(wrapper, config);
        syncTileGrids(wrapper);
        updateGroupSummaries(wrapper, config);
        updateMeasurementPreview(form, config);
        lastTotalCents = updatePrice(form, config, basePrice, compareAtPrice, fixedDiscountPct);
        try { if (refreshSummary) refreshSummary(); } catch (e) {}
      });
    });

    // Initial apply + price calculation
    applyConditions(wrapper, config);
    applyVisibilityRules(wrapper, config);
    syncTileGrids(wrapper);
    updateGroupSummaries(wrapper, config);
    updateMeasurementPreview(form, config);
    lastTotalCents = updatePrice(form, config, basePrice, compareAtPrice, fixedDiscountPct);
    try { initFromLabel(form); } catch (e) {}
    // Nunca debe impedir que se configure ni que se añada el espejo.
    try {
      refreshSummary = initSummary(form, config, function () { return lastTotalCents; });
    } catch (e) {}
    try {
      onFirstInteraction(form, function () { setStickyGate(form, false); });
    } catch (e) {
      // Sin la señal no habria forma de desbloquear el boton: se deja como estaba.
      setStickyGate(form, false);
    }

    // Bind form submit
    form.addEventListener('submit', function (e) {
      e.preventDefault();
      // e.submitter is the button that triggered the submit (modern browsers).
      // Fallback to the configurator's own ATC button if not available.
      var clickedBtn = e.submitter || form.querySelector('.configurator-add-btn');
      handleAddToCart(form, config, lastTotalCents, clickedBtn);
    });
  }

  // ---------------------------------------------------------------------------
  // Rendering
  // ---------------------------------------------------------------------------
  function renderGroups(wrapper, config, fieldsInfo) {
    fieldsInfo = fieldsInfo || {};
    var html = '';

    config.groups.forEach(function (group, index) {
      var isAccessoryGroup = isAccessoriesGroup(group);
      var fieldsHtml = isTileGridGroup(group) ? renderTileGridGroup(group) : group.fields.map(function (field, fieldIndex) {
        return renderField(field, fieldsInfo, {
          isAccessoryGroup: isAccessoryGroup,
          forceOff: isAccessoryGroup && fieldIndex === 0,
          groupLabel: group && group.label ? group.label : ''
        });
      }).join('');

      var isOpen = true; // all groups open by default
      var groupClass = getGroupLayoutClass(group);
      var stepNumber = index + 1;
      var groupIcon = getGroupIconMarkup(group);

      html += (
        '<div class="configurator-group ' + groupClass + (isOpen ? ' is-open' : '') + '">' +
          '<div class="configurator-group__header">' +
            '<span class="configurator-group__step">' + stepNumber + '</span>' +
            '<span class="configurator-group__heading-row">' +
              groupIcon +
              '<span class="configurator-group__title">' + escapeHtml(group.label) + '</span>' +
            '</span>' +
          '</div>' +
          '<div class="configurator-group__body">' +
            '<div class="configurator-group__content">' + fieldsHtml + '</div>' +
          '</div>' +
        '</div>'
      );
    });

    wrapper.innerHTML = html;
    initGallery();
    initPopovers(wrapper, fieldsInfo);
    initAccessoryControls(wrapper);
    initFieldSwatches(wrapper);
    initFieldTiles(wrapper);
    initTileGrids(wrapper);
  }

  function getGroupLayoutClass(group) {
    var keys = (group.fields || []).map(function (field) { return field.key; });
    var hasMeasureKey = keys.some(function (key) {
      return ['ancho', 'alto', 'ancho1', 'alto1', 'diametro', 'medida', 'medidas'].indexOf(key) !== -1;
    });

    if (hasMeasureKey) return 'configurator-group--measure';
    if (isTileGridGroup(group)) return 'configurator-group--stack configurator-group--tile-grid';
    if (isAccessoriesGroup(group)) return 'configurator-group--accessories configurator-group--stack';
    return 'configurator-group--stack';
  }

  function isAccessoriesGroup(group) {
    if (!group) return false;
    // El metafield puede pedir el estilo de tarjetas de accesorio para un grupo
    // que no se llame "accesorios" (p. ej. "Selecciona las luces" en THEMIS).
    if (group.layout === 'accessories') return true;
    return typeof group.label === 'string' && group.label.toLowerCase().indexOf('accesorios') !== -1;
  }

  // ---------------------------------------------------------------------------
  // Rejilla de fotos (group.layout = 'tile_grid'): cada campo es un "No / Sí" y
  // se marca tocando su foto. Ej. los accesorios de la barra multifunción LUXE.
  // Los <select> siguen dentro de cada .configurator-field, así que precio,
  // reglas de visibilidad, resumen y propiedades del carrito no cambian.
  // ---------------------------------------------------------------------------
  function isTileGridGroup(group) {
    return !!(group && group.layout === 'tile_grid');
  }

  function getTileGridOnIndex(field) {
    var options = field.options || [];
    for (var i = 0; i < options.length; i++) {
      if (!isOffOption(options[i])) return i;
    }
    return -1;
  }

  function renderTileGridGroup(group) {
    var items = group.fields.map(function (field) {
      var onIndex = getTileGridOnIndex(field);
      var onOption = onIndex >= 0 ? field.options[onIndex] : null;
      var image = onOption && typeof onOption.image === 'string' ? onOption.image.trim() : '';
      var optionsHtml = field.options.map(function (opt, optIndex) {
        return '<option value="' + escapeHtml(opt.value) + '" data-index="' + optIndex + '"' + (opt.default ? ' selected' : '') + '>'
          + escapeHtml(opt.label) + '</option>';
      }).join('');
      return (
        '<div class="configurator-field configurator-tile-grid__item">' +
          '<select id="cfg-' + escapeHtml(field.key) + '" name="' + escapeHtml(field.key) + '" ' +
            'data-config-key="' + escapeHtml(field.key) + '" ' +
            'data-affects-price="' + (field.affects_price ? 'true' : 'false') + '" ' +
            'class="configurator-tile-grid__native" tabindex="-1" aria-hidden="true">' +
            optionsHtml +
          '</select>' +
          '<button type="button" class="configurator-tile-grid__tile" data-grid-tile data-on-index="' + onIndex + '" aria-pressed="false">' +
            (image ? '<span class="configurator-tile-grid__media"><img src="' + escapeHtml(image) + '" alt="" loading="lazy"></span>' : '') +
            '<span class="configurator-tile-grid__name">' + escapeHtml(field.label) + '</span>' +
          '</button>' +
        '</div>'
      );
    }).join('');

    return (
      '<div class="configurator-tile-grid">' +
        (group.grid_label || group.grid_description
          ? '<div class="configurator-tile-grid__head">' +
              (group.grid_label ? '<span class="configurator-tile-grid__label">' + escapeHtml(group.grid_label) + '</span>' : '') +
              (group.grid_description ? '<p class="configurator-tile-grid__desc">' + escapeHtml(group.grid_description) + '</p>' : '') +
            '</div>'
          : '') +
        '<div class="configurator-tile-grid__items">' + items + '</div>' +
        '<p class="configurator-tile-grid__picked" data-grid-picked aria-live="polite"></p>' +
      '</div>'
    );
  }

  function initTileGrids(wrapper) {
    wrapper.querySelectorAll('.configurator-tile-grid').forEach(function (grid) {
      grid.querySelectorAll('[data-grid-tile]').forEach(function (tile) {
        tile.addEventListener('click', function () {
          var select = tile.parentNode.querySelector('select[data-config-key]');
          var onIndex = tile.getAttribute('data-on-index');
          if (!select || onIndex === '-1') return;
          var isOn = getSelectedOptionIndex(select) === String(onIndex);
          var offOption = Array.prototype.slice.call(select.options).find(function (o) {
            return String(o.getAttribute('data-index')) !== String(onIndex);
          });
          selectOptionByIndex(select, isOn ? (offOption ? offOption.getAttribute('data-index') : 0) : onIndex);
          select.dispatchEvent(new Event('change', { bubbles: true }));
          syncTileGrids(wrapper);
        });
      });
    });
    syncTileGrids(wrapper);
  }

  // También tras las reglas de visibilidad: al ocultar un campo su <select>
  // vuelve a "No" sin evento change.
  function syncTileGrids(wrapper) {
    wrapper.querySelectorAll('.configurator-tile-grid').forEach(function (grid) {
      var names = [];
      grid.querySelectorAll('.configurator-tile-grid__item').forEach(function (item) {
        var select = item.querySelector('select[data-config-key]');
        var tile = item.querySelector('[data-grid-tile]');
        if (!select || !tile) return;
        var isOn = item.style.display !== 'none' && getSelectedOptionIndex(select) === tile.getAttribute('data-on-index');
        tile.setAttribute('aria-pressed', isOn ? 'true' : 'false');
        tile.classList.toggle('is-selected', isOn);
        if (isOn) names.push(tile.textContent.trim().toLowerCase());
      });
      var picked = grid.querySelector('[data-grid-picked]');
      if (picked) {
        picked.classList.toggle('is-empty', !names.length);
        picked.textContent = names.length
          ? names.length + (names.length === 1 ? ' elegido: ' : ' elegidos: ') + names.join(', ')
          : 'Ninguno elegido';
      }
    });
  }

  function isLightsGroup(group) {
    var label = group && typeof group.label === 'string' ? group.label.toLowerCase() : '';
    return label.indexOf('luz') !== -1 || label.indexOf('luces') !== -1 || label.indexOf('iluminaci') !== -1;
  }

  function getGroupIconMarkup(group) {
    if (isLightsGroup(group)) {
      return (
        '<span class="configurator-group__icon" aria-hidden="true">' +
          '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8">' +
            '<circle cx="12" cy="12" r="3.5"/>' +
            '<path d="M12 3v2.5M12 18.5V21M3 12h2.5M18.5 12H21M5.6 5.6l1.8 1.8M16.6 16.6l1.8 1.8M5.6 18.4l1.8-1.8M16.6 7.4l1.8-1.8" stroke-linecap="round"/>' +
          '</svg>' +
        '</span>'
      );
    }
    if (isAccessoriesGroup(group)) {
      return (
        '<span class="configurator-group__icon" aria-hidden="true">' +
          '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8">' +
            '<circle cx="12" cy="12" r="3.2"/>' +
            '<path d="M19 12a7 7 0 0 0-.08-1l2.05-1.6-2-3.46-2.46.8a7.55 7.55 0 0 0-1.72-1l-.38-2.55h-4l-.38 2.55a7.55 7.55 0 0 0-1.72 1l-2.46-.8-2 3.46L5.08 11a8.6 8.6 0 0 0 0 2l-2.05 1.6 2 3.46 2.46-.8a7.55 7.55 0 0 0 1.72 1l.38 2.55h4l.38-2.55a7.55 7.55 0 0 0 1.72-1l2.46.8 2-3.46L18.92 13c.05-.33.08-.66.08-1z" stroke-linejoin="round"/>' +
          '</svg>' +
        '</span>'
      );
    }

    return (
      '<span class="configurator-group__icon" aria-hidden="true">' +
        '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8">' +
          '<path d="M4 8.5h16v7H4z" stroke-linejoin="round"/>' +
          '<path d="M8 8.5v4m4-4v4m4-4v4" stroke-linecap="round"/>' +
        '</svg>' +
      '</span>'
    );
  }

  // ---------------------------------------------------------------------------
  // Groups stay open. The old accordion header was removed to avoid covering options.
  // ---------------------------------------------------------------------------
  function initAccordions(wrapper) {
    if (!wrapper) return;
  }

  // ---------------------------------------------------------------------------
  // Gallery — thumbnail switching
  // ---------------------------------------------------------------------------
  function initGallery() {
    var mainImg = document.getElementById('cfg-main-img');
    if (!mainImg) return;

    document.querySelectorAll('.cfg-gallery__thumb').forEach(function (thumb) {
      thumb.addEventListener('click', function () {
        var src = thumb.getAttribute('data-media-src');
        var alt = thumb.getAttribute('data-media-alt');

        // Highlight active thumb
        document.querySelectorAll('.cfg-gallery__thumb').forEach(function (t) {
          t.classList.remove('is-active');
        });
        thumb.classList.add('is-active');

        // Swap main image with fade
        mainImg.classList.add('is-loading');
        var newImg = new Image();
        newImg.onload = function () {
          mainImg.src = src;
          if (alt) mainImg.alt = alt;
          mainImg.classList.remove('is-loading');
        };
        newImg.onerror = function () {
          mainImg.src = src;
          mainImg.classList.remove('is-loading');
        };
        newImg.src = src;
      });
    });
  }

  // ---------------------------------------------------------------------------
  // Info popovers — ⓘ button next to each field label
  // ---------------------------------------------------------------------------
  function initPopovers(wrapper, fieldsInfo) {
    if (!fieldsInfo || !Object.keys(fieldsInfo).length) return;

    // One shared popover element, repositioned on each open
    var popover = document.getElementById('cfg-info-popover');
    if (!popover) {
      popover = document.createElement('div');
      popover.id = 'cfg-info-popover';
      popover.className = 'cfg-info-popover';
      popover.setAttribute('role', 'tooltip');
      popover.setAttribute('aria-hidden', 'true');
      document.body.appendChild(popover);
    }

    var activeBtn = null;

    // La "i" solo enseña lo que no esta ya a la vista. Si el texto del campo se
    // pinta bajo su titulo, el popover no repite ni el titulo ni el texto y se
    // queda con la imagen.
    function popoverContent(btn, info) {
      var fieldWrap = btn.closest('.configurator-field');
      var textShown = !!(fieldWrap && fieldWrap.querySelector('.configurator-accessory-desc, .configurator-field__desc'));
      return {
        heading: textShown ? '' : (info.heading || ''),
        image: info.image || '',
        text: textShown ? '' : (info.text || '')
      };
    }

    // Sin imagen ni texto propio la "i" no tiene nada que añadir: se quita.
    wrapper.querySelectorAll('.cfg-info-btn').forEach(function (btn) {
      var info = fieldsInfo[btn.getAttribute('data-info-key')];
      var content = info ? popoverContent(btn, info) : null;
      if (!content || (!content.image && !content.text)) btn.remove();
    });

    function openPopover(btn, info) {
      var content = popoverContent(btn, info);
      var layoutClass = getEditorialLayoutClass(info, fieldsInfo);
      var headingHtml = content.heading
        ? '<h4 class="cfg-info-popover__title">' + escapeHtml(content.heading) + '</h4>'
        : '';
      var imgHtml = content.image
        ? '<img class="cfg-info-popover__img" src="' + escapeHtml(content.image) + '" alt="" loading="lazy">'
        : '';
      var textHtml = content.text
        ? renderRichText(content.text, 'cfg-info-popover__text')
        : '';
      var copyHtml = headingHtml || textHtml
        ? '<div class="cfg-info-popover__copy">' + headingHtml + textHtml + '</div>'
        : '';

      popover.innerHTML =
        '<button type="button" class="cfg-info-popover__close" aria-label="Cerrar">&times;</button>' +
        (imgHtml || copyHtml
          ? '<div class="cfg-info-popover__body' + (imgHtml ? ' cfg-info-popover__body--has-img' : '') + (imgHtml && !copyHtml ? ' cfg-info-popover__body--only-img' : '') + ' ' + layoutClass + '">' +
              imgHtml + copyHtml +
            '</div>'
          : '');

      popover.setAttribute('aria-hidden', 'false');
      popover.classList.add('is-visible');
      btn.setAttribute('aria-expanded', 'true');
      activeBtn = btn;

      positionPopover(btn);

      popover.querySelector('.cfg-info-popover__close').addEventListener('click', closePopover);
    }

    function closePopover() {
      popover.classList.remove('is-visible');
      popover.setAttribute('aria-hidden', 'true');
      if (activeBtn) {
        activeBtn.setAttribute('aria-expanded', 'false');
        activeBtn = null;
      }
    }

    function positionPopover(btn) {
      var rect = btn.getBoundingClientRect();
      var scrollY = window.scrollY || window.pageYOffset;
      var scrollX = window.scrollX || window.pageXOffset;

      // Match width and horizontal position to the configurator column so the
      // tooltip never causes horizontal scroll on narrow viewports.
      var col = btn.closest('.configurator-form') || btn.closest('.configurator-wrapper');
      var top = rect.bottom + scrollY + 8;
      var left, width;
      if (col) {
        var colRect = col.getBoundingClientRect();
        left = colRect.left + scrollX;
        width = colRect.width;
      } else {
        left = rect.left + scrollX;
        width = Math.min(520, window.innerWidth - 32);
        var maxLeft = window.innerWidth - width - 16;
        if (left > maxLeft) left = maxLeft;
        if (left < 8) left = 8;
      }

      popover.style.top   = top + 'px';
      popover.style.left  = left + 'px';
      popover.style.width = width + 'px';
    }

    wrapper.addEventListener('click', function (e) {
      var btn = e.target.closest('.cfg-info-btn');
      if (!btn) return;

      // Toggle: clicking the same button closes
      if (btn === activeBtn) {
        closePopover();
        return;
      }

      var key = btn.getAttribute('data-info-key');
      var info = fieldsInfo[key];
      if (!info) return;

      openPopover(btn, info);
    });

    // Close on outside click
    document.addEventListener('click', function (e) {
      if (!activeBtn) return;
      if (popover.contains(e.target) || e.target.closest('.cfg-info-btn')) return;
      closePopover();
    });

    // Close on Escape
    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' && activeBtn) closePopover();
    });
  }

  // ---------------------------------------------------------------------------
  // Accordion summaries — update collapsed group header with selected values
  // ---------------------------------------------------------------------------
  function updateGroupSummaries(wrapper, config) {
    var groups = wrapper.querySelectorAll('.configurator-group');
    groups.forEach(function (groupEl, idx) {
      var summaryEl = groupEl.querySelector('.configurator-group__summary');
      if (!summaryEl) return;

      var group = config.groups[idx];
      if (!group) return;

      var parts = [];
      group.fields.forEach(function (field) {
        var el = groupEl.querySelector('[data-config-key="' + field.key + '"]');
        if (!el) return;
        if (el.closest('.configurator-field') && el.closest('.configurator-field').style.display === 'none') return;

        if (el.tagName === 'SELECT') {
          var opt = el.options[el.selectedIndex];
          if (opt && opt.value && opt.value !== '0' && opt.text.toLowerCase() !== 'no') {
            parts.push(opt.text);
          }
        } else if (el.type === 'checkbox' && el.checked) {
          parts.push(field.label);
        }
      });

      summaryEl.textContent = parts.slice(0, 3).join(', ');
    });
  }

  function renderField(field, fieldsInfo, renderOptions) {
    fieldsInfo = fieldsInfo || {};
    renderOptions = renderOptions || {};
    var info = fieldsInfo[field.key];
    var displayLabel = getEditorialHeading(field, info);
    var editorialClass = getEditorialLayoutClass(info, fieldsInfo);
    var isPreviewMeasureField = ['ancho', 'alto', 'ancho1', 'alto1', 'diametro', 'medida', 'medidas'].indexOf(field.key) !== -1;
    var infoBtn = (info && (info.heading || info.text || info.image))
      ? '<button type="button" class="cfg-info-btn" data-info-key="' + escapeHtml(field.key) + '" aria-label="Más información sobre ' + escapeHtml(displayLabel) + '" tabindex="0">i</button>'
      : '';

    if (field.type === 'switch') {
      var switchDescription = getSwitchDescription(field, info);
      var switchHelp = (switchDescription || (info && info.image))
        ? (
          '<div class="configurator-switch-help">' +
            '<div class="configurator-switch-help__copy">' +
              '<span class="configurator-switch-help__title">' + escapeHtml(displayLabel) + '</span>' +
            (switchDescription ? renderRichText(switchDescription, '') : '') +
            '</div>' +
            ((info && info.image)
              ? '<span class="configurator-switch-help__media"><img src="' + escapeHtml(info.image) + '" alt="" loading="lazy"></span>'
              : '') +
          '</div>'
        )
        : '';

      return (
        '<div class="configurator-field configurator-field--switch ' + editorialClass + '">' +
          '<div class="configurator-field__surface">' +
            '<div class="configurator-switch-row">' +
              '<div class="configurator-switch-main">' + switchHelp + '</div>' +
              '<label class="configurator-switch-label" aria-label="' + escapeHtml(field.label) + '">' +
                '<input type="checkbox" ' +
                  'id="cfg-' + escapeHtml(field.key) + '" ' +
                  'name="' + escapeHtml(field.key) + '" ' +
                  'data-config-key="' + escapeHtml(field.key) + '" ' +
                  'data-affects-price="false" ' +
                  'class="configurator-switch-input"' +
                '>' +
                '<span class="configurator-switch-toggle"></span>' +
              '</label>' +
            '</div>' +
          '</div>' +
        '</div>'
      );
    }

    if (field.type !== 'dropdown') return '';

    if (renderOptions.isAccessoryGroup && !isPreviewMeasureField) {
      return renderAccessoryField(field, infoBtn, renderOptions.forceOff, info, fieldsInfo);
    }

    if (isColorAccessoryField(field, renderOptions.groupLabel)) {
      return renderColorSwatchField(field, infoBtn, info, fieldsInfo);
    }

    if (!isPreviewMeasureField && Array.isArray(field.options) && field.options.length > 0 && field.options.length <= 8) {
      return renderTileSelectField(field, infoBtn, info, fieldsInfo);
    }

    var fieldDescription = getFieldDescription(field, info);
    var optionsHtml = field.options.map(function (opt) {
      var selected = opt.default ? ' selected' : '';
      return '<option value="' + escapeHtml(opt.value) + '"' + selected + '>'
        + escapeHtml(opt.label) + '</option>';
    }).join('');

    return (
      '<div class="configurator-field ' + editorialClass + (isPreviewMeasureField ? ' configurator-field--hidden-measure' : '') + '">' +
        '<div class="configurator-field__surface">' +
          '<div class="configurator-field__heading">' +
            '<div class="configurator-field__label-row">' +
              '<label for="cfg-' + escapeHtml(field.key) + '">' + escapeHtml(displayLabel) + '</label>' +
              infoBtn +
            '</div>' +
            (fieldDescription ? renderRichText(fieldDescription, 'configurator-field__desc') : '') +
          '</div>' +
          '<select ' +
            'id="cfg-' + escapeHtml(field.key) + '" ' +
            'name="' + escapeHtml(field.key) + '" ' +
            'data-config-key="' + escapeHtml(field.key) + '" ' +
            'data-affects-price="' + (field.affects_price ? 'true' : 'false') + '"' +
          '>' +
          optionsHtml +
          '</select>' +
        '</div>' +
      '</div>'
    );
  }

  function renderTileSelectField(field, infoBtn, info, fieldsInfo) {
    var displayLabel = getEditorialHeading(field, info);
    var editorialClass = getEditorialLayoutClass(info, fieldsInfo);
    var selectedIndex = Math.max(0, field.options.findIndex(function (opt) { return opt.default; }));
    if (selectedIndex < 0) selectedIndex = 0;
    var selectedOption = field.options[selectedIndex] || field.options[0] || {};
    var fieldDescription = getFieldDescription(field, info);
    var optionsHtml = field.options.map(function (opt, optIndex) {
      var selected = optIndex === selectedIndex ? ' selected' : '';
      return '<option value="' + escapeHtml(opt.value) + '" data-index="' + optIndex + '"' + selected + '>'
        + escapeHtml(opt.label) + '</option>';
    }).join('');
    var buttonsHtml = field.options.map(function (opt, optIndex) {
      var isSelected = optIndex === selectedIndex;
      return (
        '<button type="button" class="configurator-tile-option' + (isSelected ? ' is-selected' : '') + '" data-field-tile data-index="' + optIndex + '" data-value="' + escapeHtml(opt.value) + '">' +
          '<span class="configurator-tile-option__label">' + escapeHtml(opt.label) + '</span>' +
        '</button>'
      );
    }).join('');

    return (
      '<div class="configurator-field configurator-field--tile-select ' + editorialClass + '">' +
        '<div class="configurator-field__surface">' +
          '<div class="configurator-field__heading configurator-field__heading--stack">' +
            '<div class="configurator-field__label-row">' +
              '<label for="cfg-' + escapeHtml(field.key) + '">' + escapeHtml(displayLabel) + '</label>' +
              infoBtn +
            '</div>' +
            '<p class="configurator-tile-select__current"><span class="configurator-tile-select__current-label">' + escapeHtml(displayLabel) + ':</span> <span data-field-current>' + escapeHtml(selectedOption.label || '') + '</span></p>' +
            (fieldDescription ? renderRichText(fieldDescription, 'configurator-field__desc') : '') +
          '</div>' +
          '<select ' +
            'id="cfg-' + escapeHtml(field.key) + '" ' +
            'name="' + escapeHtml(field.key) + '" ' +
            'data-config-key="' + escapeHtml(field.key) + '" ' +
            'data-affects-price="' + (field.affects_price ? 'true' : 'false') + '" ' +
            'class="configurator-accessory-native"' +
          '>' +
          optionsHtml +
          '</select>' +
          '<div class="configurator-tile-options">' + buttonsHtml + '</div>' +
        '</div>' +
      '</div>'
    );
  }

  function getSwitchDescription(field, info) {
    if (info && info.text) return info.text;
    if (!field || !field.key) return '';

    var normalizedKey = String(field.key).toLowerCase();
    var normalizedLabel = String(field.label || '').toLowerCase();
    if (normalizedKey.indexOf('posibilidad_vertical') !== -1 || normalizedLabel.indexOf('posibilidad vertical') !== -1) {
      return 'Permite instalar el espejo tambien en posicion vertical si lo necesitas.';
    }

    return '';
  }

  function getEditorialHeading(field, info) {
    if (info && info.heading) return info.heading;
    return field && field.label ? field.label : '';
  }

  function getEditorialLayoutClass(info, fieldsInfo) {
    var layout = info && info.layout && info.layout !== 'default'
      ? info.layout
      : fieldsInfo && fieldsInfo._settings && fieldsInfo._settings.layout_default;

    if (layout !== 'columns') layout = 'rows';
    return 'configurator-field--editorial-' + layout;
  }

  function getOptionInfo(field, option, info) {
    if (!field || !option || !info || !info.options) return null;
    var optionId = getOptionEditorialId(field.key, option.label);
    if (!optionId || !info.options[optionId]) return null;
    var optionInfo = info.options[optionId];
    if (!optionInfo.text && !optionInfo.image && !optionInfo.color) return null;
    return optionInfo;
  }

  function getOptionEditorialId(fieldKey, label) {
    var key = String(fieldKey || '').toLowerCase();
    var text = normalizeOptionLabel(label);

    if (key === 'sensor_on_off') {
      if (text.indexOf('tactil') !== -1) return 'sensor_tactil';
      if (text.indexOf('infrarrojo') !== -1) return 'sensor_infrarrojo';
      if (text.indexOf('reloj') !== -1 || text.indexOf('temp') !== -1) return 'sensor_reloj_temp';
      if (text.indexOf('intensidad') !== -1) return 'sensor_cambio_intensidad';
      if (text.indexOf('cambio de luz') !== -1) return 'sensor_cambio_luz';
    }
    if (key === 'lamina_antivaho') {
      if (text.indexOf('con sensor') !== -1) return 'con_sensor';
      if (text.indexOf('sin sensor') !== -1) return 'sin_sensor';
    }
    if (key === 'espejo_aumento') {
      if (text.indexOf('fijacion lateral') !== -1) return 'x3_fijacion_lateral';
      if (text.indexOf('regulable') !== -1 || text.indexOf('imantado') !== -1) return 'x3_regulable_imantado';
      if (text.indexOf('con luz') !== -1 && text.indexOf('sensor') !== -1) return 'con_luz_sensor';
      if (text.indexOf('con luz') !== -1) return 'con_luz';
      if (text.indexOf('iman') !== -1) return 'sin_luz_iman';
      if (text.indexOf('sin luz') !== -1) return 'sin_luz';
    }
    if (key === 'radio_control') {
      if (text.indexOf('meteorologica') !== -1) return 'estacion_meteorologica';
      if (text.indexOf('display') !== -1 || text.indexOf('radio') !== -1) return 'display_radio';
      if (text.indexOf('digital') !== -1) return 'estacion_digital';
      if (text === 'si') return 'si';
    }
    if (key === 'reloj_temperatura' || key === 'altavoces' || key === 'enchufe') {
      if (text === 'si') return 'si';
    }
    if (key === 'tipo_luz' || key === 'tipo_luz_espejo_aumento' || key === 'tipo_luz_ambiental' || key === 'tipo_luz_frontal') {
      if (text.indexOf('calida') !== -1) return 'calida';
      if (text.indexOf('neutra') !== -1) return 'neutra';
      if (text.indexOf('fria') !== -1) return 'fria';
      if (text.indexOf('biled') !== -1 || text.indexOf('3 colores') !== -1 || text.indexOf('3 tonos') !== -1) return 'biled_3_colores';
    }
    if (key === 'difusor') {
      if (text.indexOf('sin difusor') !== -1) return 'sin_difusor';
      if (text.indexOf('con difusor') !== -1) return 'con_difusor';
    }
    if (key === 'marco') {
      if (text.indexOf('negro') !== -1 && text.indexOf('mate') !== -1) return 'negro_mate';
      if (text.indexOf('aluminio') !== -1) return 'aluminio';
    }
    if (key === 'pintura_marco') {
      if (text.indexOf('oro rosa') !== -1 || text.indexOf('rosa') !== -1) return 'oro_rosa';
      if (text.indexOf('negro') !== -1) return 'negro';
      if (text.indexOf('dorado') !== -1 || text.indexOf('oro') !== -1) return 'dorado';
      if (text.indexOf('blanco') !== -1) return 'blanco';
    }
    if (key === 'color') {
      if (text.indexOf('paleta ral') !== -1 || text.indexOf('ral') !== -1) return 'paleta_ral';
      if (text.indexOf('oro rosa') !== -1 || text.indexOf('rosa') !== -1) return 'oro_rosa';
      if (text.indexOf('negro') !== -1 && text.indexOf('mate') !== -1) return 'negro_mate';
      if (text.indexOf('blanco') !== -1 && text.indexOf('mate') !== -1) return 'blanco_mate';
      if (text.indexOf('cobre') !== -1 && text.indexOf('mate') !== -1) return 'cobre_mate';
      if (text.indexOf('oro') !== -1 && text.indexOf('mate') !== -1) return 'oro_mate';
      if (text.indexOf('inox') !== -1) return 'inox_mate';
      if (text.indexOf('plata') !== -1) return 'plata_mate';
      if (text.indexOf('bronce') !== -1) return 'bronce_mate';
    }
    if (key === 'color_correa_cuero') {
      if (text.indexOf('negro') !== -1) return 'cuero_negro';
      if (text.indexOf('marron') !== -1 || text.indexOf('marrón') !== -1) return 'cuero_marron';
    }

    return '';
  }

  function normalizeOptionLabel(label) {
    return String(label || '')
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/\s+/g, ' ')
      .trim();
  }

  function renderColorSwatchField(field, infoBtn, info, fieldsInfo) {
    var displayLabel = getEditorialHeading(field, info);
    var editorialClass = getEditorialLayoutClass(info, fieldsInfo);
    var ralPdfUrl = fieldsInfo && fieldsInfo._settings && fieldsInfo._settings.ral_pdf_url
      ? fieldsInfo._settings.ral_pdf_url
      : '';
    var selectedIndex = Math.max(0, field.options.findIndex(function (opt) { return opt.default; }));
    var optionsHtml = field.options.map(function (opt, optIndex) {
      var selected = optIndex === selectedIndex ? ' selected' : '';
      return '<option value="' + escapeHtml(opt.value) + '" data-index="' + optIndex + '"' + selected + '>'
        + escapeHtml(opt.label) + '</option>';
    }).join('');
    var swatchesHtml = field.options.map(function (opt, optIndex) {
      var isSelected = optIndex === selectedIndex;
      var optionInfo = getOptionInfo(field, opt, info);
      var swatch = getAccessoryColorSwatch(opt, optionInfo);
      return (
        '<button type="button" class="configurator-accessory-swatch' + (isSelected ? ' is-selected' : '') + '" data-field-swatch data-index="' + optIndex + '" data-value="' + escapeHtml(opt.value) + '" aria-label="' + escapeHtml(opt.label) + '" title="' + escapeHtml(opt.label) + '">' +
          '<span class="configurator-accessory-swatch__chip" style="' + escapeHtml(swatch.style) + '">' +
            '<span class="configurator-accessory-swatch__check" aria-hidden="true">' + (swatch.check || '&#10003;') + '</span>' +
          '</span>' +
          '<span class="configurator-accessory-swatch__label">' + escapeHtml(opt.label) + '</span>' +
        '</button>'
      );
    }).join('');

    return (
      '<div class="configurator-field configurator-field--swatch-select ' + editorialClass + '">' +
        '<div class="configurator-field__surface">' +
          '<div class="configurator-field__heading">' +
            '<div class="configurator-field__label-row">' +
              '<label for="cfg-' + escapeHtml(field.key) + '">' + escapeHtml(displayLabel) + '</label>' +
              infoBtn +
            '</div>' +
          '</div>' +
          '<select ' +
            'id="cfg-' + escapeHtml(field.key) + '" ' +
            'name="' + escapeHtml(field.key) + '" ' +
            'data-config-key="' + escapeHtml(field.key) + '" ' +
            'data-affects-price="' + (field.affects_price ? 'true' : 'false') + '" ' +
            'class="configurator-accessory-native"' +
          '>' +
          optionsHtml +
          '</select>' +
          '<div class="configurator-accessory-options configurator-accessory-options--swatches">' +
            swatchesHtml +
          '</div>' +
          (field.key === 'color' ? renderRalInput(ralPdfUrl) : '') +
        '</div>' +
      '</div>'
    );
  }

  function renderRalInput(pdfUrl) {
    var link = pdfUrl
      ? '<a class="configurator-ral__link" href="' + escapeHtml(pdfUrl) + '" target="_blank" rel="noopener">Consultar carta RAL</a>'
      : '<span class="configurator-ral__link configurator-ral__link--muted">Carta RAL pendiente</span>';

    return (
      '<div class="configurator-ral" data-ral-panel hidden>' +
        '<label class="configurator-ral__label" for="cfg-color-ral">Código RAL</label>' +
        '<div class="configurator-ral__row">' +
          '<input id="cfg-color-ral" class="configurator-ral__input" type="text" maxlength="12" placeholder="Ej. RAL 9010" autocomplete="off" data-ral-input>' +
          link +
        '</div>' +
        '<p class="configurator-ral__error" data-ral-error hidden>Introduce el código RAL para continuar.</p>' +
      '</div>'
    );
  }

  function getFieldDescription(field, info) {
    if (info && info.text) return info.text;
    if (!field) return '';

    var key = String(field.key || '').toLowerCase();
    var label = String(field.label || '').toLowerCase();

    if (key.indexOf('led') !== -1 || label.indexOf('led') !== -1) {
      return 'Define la densidad de iluminación para ajustar el rendimiento y el acabado del espejo.';
    }

    return '';
  }

  function renderAccessoryField(field, infoBtn, forceOff, info, fieldsInfo) {
    var displayLabel = getEditorialHeading(field, info);
    var editorialClass = getEditorialLayoutClass(info, fieldsInfo);
    var offOption = getOffOption(field.options);
    var hasOffOption = !!offOption;
    var selectedIndex = getInitialAccessoryIndex(field.options, forceOff);
    var selectedOption = field.options[selectedIndex] || field.options[0] || {};
    var selectedIsOff = hasOffOption && isOffOption(selectedOption);
    var isActive = !selectedIsOff;
    var isSwatchField = isColorAccessoryField(field, '');
    var fieldDescription = getAccessoryFieldDescription(field, info);
    var toggleDisabled = hasOffOption ? '' : ' disabled aria-disabled="true"';
    var optionsHtml = field.options.map(function (opt, optIndex) {
      var selected = optIndex === selectedIndex ? ' selected' : '';
      return '<option value="' + escapeHtml(opt.value) + '" data-index="' + optIndex + '"' + selected + '>'
        + escapeHtml(opt.label) + '</option>';
    }).join('');
    var cardsHtml = field.options.map(function (opt, optIndex) {
      if (isOffOption(opt)) return '';
      var isSelected = optIndex === selectedIndex;
      var optionInfo = getOptionInfo(field, opt, info);
      // El texto propio de la opcion en el metafield manda sobre el del editor
      // del tema: el del tema es comun a todas las fichas (p. ej. en un espejo
      // sin luz, LISA, el antivaho no se enciende "con la luz del espejo").
      var ownDescription = typeof opt.description === 'string' && opt.description.trim() ? opt.description : '';
      var optionDescription = ownDescription
        || (optionInfo && optionInfo.text ? optionInfo.text : getAccessoryDescription(field, opt));
      // La imagen puede venir del editor del tema (optionInfo) o del propio
      // metafield (opt.image; opt.image_fit = 'contain' para iconos/logos).
      var optionImageUrl = (optionInfo && optionInfo.image)
        || (typeof opt.image === 'string' && opt.image.trim() ? opt.image.trim() : '');
      var hasOptionImage = !!optionImageUrl;
      var hasOptionColor = !!(optionInfo && optionInfo.color);
      var optionImageClass = hasOptionImage ? ' has-custom-image' : (hasOptionColor ? ' has-custom-color' : ' has-no-media');
      if (hasOptionImage && opt.image_fit === 'contain') optionImageClass += ' has-contain-image';
      var optionMedia = hasOptionImage
        ? '<img src="' + escapeHtml(optionImageUrl) + '" alt="" loading="lazy">'
        : hasOptionColor
          ? '<span class="configurator-accessory-option__color" style="' + escapeHtml(getOptionColorStyle(optionInfo.color)) + '" aria-hidden="true"></span>'
          : (
            '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5">' +
              '<path d="M5 18h14M8 18l1.5-5h5L16 18M9 13V6h6v7M10.5 8.5h3" stroke-linecap="round" stroke-linejoin="round"/>' +
            '</svg>'
          );
      if (isSwatchField) {
        var swatch = getAccessoryColorSwatch(opt, optionInfo);
        return (
          '<button type="button" class="configurator-accessory-swatch' + (isSelected ? ' is-selected' : '') + '" data-accessory-option data-index="' + optIndex + '" data-value="' + escapeHtml(opt.value) + '" aria-label="' + escapeHtml(opt.label) + '" title="' + escapeHtml(opt.label) + '">' +
            '<span class="configurator-accessory-swatch__chip" style="' + escapeHtml(swatch.style) + '">' +
              '<span class="configurator-accessory-swatch__check" aria-hidden="true">' + (swatch.check || '&#10003;') + '</span>' +
            '</span>' +
            '<span class="configurator-accessory-swatch__label">' + escapeHtml(opt.label) + '</span>' +
          '</button>'
        );
      }
      return (
        '<button type="button" class="configurator-accessory-option' + optionImageClass + (isSelected ? ' is-selected' : '') + '" data-accessory-option data-index="' + optIndex + '" data-value="' + escapeHtml(opt.value) + '">' +
          '<span class="configurator-accessory-option__icon" aria-hidden="true">' + getAccessoryOptionIcon(field, opt) + '</span>' +
          '<span class="configurator-accessory-option__copy">' +
            '<span class="configurator-accessory-option__title">' + escapeHtml(opt.label) + '</span>' +
            renderRichText(optionDescription, 'configurator-accessory-option__text') +
          '</span>' +
          // Sin foto ni color (p. ej. espejos sin luz) la tarjeta es solo texto
          ((hasOptionImage || hasOptionColor)
            ? '<span class="configurator-accessory-option__media" aria-hidden="true">' + optionMedia + '</span>'
            : '') +
        '</button>'
      );
    }).join('');

    return (
        '<div class="configurator-field configurator-field--accessory ' + editorialClass + (isActive ? ' is-active' : '') + (!hasOffOption ? ' is-required' : '') + (isSwatchField ? ' configurator-field--swatches' : '') + '" data-accessory-field>' +
        '<div class="configurator-field__surface">' +
          '<div class="configurator-accessory-head">' +
            '<div class="configurator-accessory-copy">' +
              '<div class="configurator-field__label-row">' +
                '<label for="cfg-' + escapeHtml(field.key) + '">' + escapeHtml(displayLabel) + '</label>' +
                infoBtn +
              '</div>' +
            (fieldDescription ? renderRichText(fieldDescription, 'configurator-accessory-desc') : '') +
            '</div>' +
            '<label class="configurator-accessory-toggle">' +
              '<input type="checkbox" class="configurator-accessory-toggle__input" data-accessory-toggle' + (isActive ? ' checked' : '') + toggleDisabled + '>' +
              '<span class="configurator-accessory-toggle__track"></span>' +
            '</label>' +
          '</div>' +
          '<select ' +
            'id="cfg-' + escapeHtml(field.key) + '" ' +
            'name="' + escapeHtml(field.key) + '" ' +
            'data-config-key="' + escapeHtml(field.key) + '" ' +
            'data-affects-price="' + (field.affects_price ? 'true' : 'false') + '" ' +
            'class="configurator-accessory-native"' +
          '>' +
          optionsHtml +
          '</select>' +
          '<div class="configurator-accessory-options' + (isSwatchField ? ' configurator-accessory-options--swatches' : '') + '" data-accessory-options' + (isActive ? '' : ' hidden') + '>' +
            cardsHtml +
          '</div>' +
        '</div>' +
      '</div>'
    );
  }

  function getInitialAccessoryIndex(options, forceOff) {
    options = options || [];
    var offIndex = options.findIndex(isOffOption);
    if (forceOff && offIndex !== -1) return offIndex;

    var defaultIndex = options.findIndex(function (opt) { return opt.default; });
    if (defaultIndex !== -1) return defaultIndex;

    if (offIndex !== -1) return offIndex;
    return 0;
  }

  function getOffOption(options) {
    return (options || []).find(isOffOption);
  }

  function isOffOption(option) {
    if (!option || typeof option.label !== 'string') return false;
    var label = option.label.trim().toLowerCase();
    return label === 'no' || label === 'sin' || label === 'ninguno';
  }

  function getAccessoryDescription(field, option) {
    // Texto propio de la opcion en el metafield (p. ej. modos de encendido de THEMIS)
    if (option && typeof option.description === 'string' && option.description.trim()) return option.description;
    var fieldKey = String(field.key || '').toLowerCase();
    var label = (option.label || '').toLowerCase();
    if (fieldKey.indexOf('sensor') !== -1 || label.indexOf('sensor') !== -1) {
      if (label.indexOf('táctil') !== -1 || label.indexOf('tactil') !== -1) return 'Botón táctil integrado.';
      if (label.indexOf('infrarrojo') !== -1) return 'Encendido por proximidad sin contacto físico.';
      if (label.indexOf('reloj') !== -1 || label.indexOf('temp') !== -1) return 'Sensor con reloj y temperatura integrados.';
    }
    if (fieldKey.indexOf('lamina') !== -1 || fieldKey.indexOf('antivaho') !== -1 || label.indexOf('antivaho') !== -1) {
      if (label === 'sí' || label === 'si') return 'Sistema desempañador continuo.';
      if (label.indexOf('sin sensor') !== -1) return 'El antivaho se enciende cuando se enciende la luz del espejo.';
      if (label.indexOf('sensor') !== -1) return 'El antivaho se enciende con su propio sensor táctil, aparte de la luz.';
    }
    if ((fieldKey.indexOf('aumento') !== -1 && fieldKey.indexOf('tipo_luz') === -1) || label.indexOf('aumento') !== -1) {
      return 'Zona ampliada para tareas de precisión.';
    }
    if (fieldKey.indexOf('digital') !== -1 || label.indexOf('digital') !== -1) {
      return 'Información visible en el propio espejo.';
    }
    if (fieldKey.indexOf('altavoz') !== -1 || label.indexOf('bluetooth') !== -1) {
      return 'Audio integrado con conexión Bluetooth.';
    }
    if (field.key === 'tipo_luz' || field.key === 'tipo_luz_ambiental' || field.key === 'tipo_luz_frontal' || field.key === 'tipo_luz_espejo_aumento') {
      if (label.indexOf('cálida') !== -1 || label.indexOf('calida') !== -1) return 'Luz amarilla y acogedora, ideal para ambientes relajantes.';
      if (label.indexOf('neutra') !== -1) return 'Equilibrio natural para baños y rutinas de uso diario.';
      if (label.indexOf('fría') !== -1 || label.indexOf('fria') !== -1) return 'Luz más blanca y precisa para máxima claridad.';
      return 'Permite alternar tonos de luz según el momento.';
    }
    return 'Añade esta opción al espejo y ajusta el precio automáticamente.';
  }

  function getAccessoryFieldDescription(field, info) {
    // Igual que en las opciones: el texto del metafield manda sobre el del editor.
    if (field && typeof field.description === 'string' && field.description.trim()) return field.description;
    if (info && info.text) return info.text;
    if (!field) return '';

    var key = String(field.key || '').toLowerCase();
    var label = String(field.label || '').toLowerCase();

    if (isColorAccessoryField(field, '')) {
      return '';
    }

    if (key.indexOf('sensor') !== -1 || label.indexOf('sensor') !== -1) {
      return 'Activa el sistema de encendido por sensor para mostrar las opciones disponibles.';
    }
    if (key.indexOf('lamina') !== -1 || label.indexOf('antivaho') !== -1) {
      return 'Evita que el espejo se empañe tras la ducha para mantener siempre una vision clara.';
    }
    if (key.indexOf('aumento') !== -1 || label.indexOf('aumento') !== -1) {
      return 'Añade una zona de ampliacion para facilitar maquillaje, afeitado o detalles de precision.';
    }
    if (key.indexOf('digital') !== -1 || label.indexOf('digital') !== -1) {
      return 'Integra reloj, temperatura u otras funciones digitales directamente en el espejo.';
    }
    if (key.indexOf('altavoz') !== -1 || label.indexOf('bluetooth') !== -1) {
      return 'Reproduce musica o audio desde el movil con conexion Bluetooth integrada.';
    }
    if (key === 'tipo_luz_ambiental') {
      return 'Halo de luz perimetral hacia la pared. Con interruptor de pared va en un solo tono; con sensor puedes elegir la tira BiLED y cambiar entre cálida, neutra y fría.';
    }
    if (key === 'tipo_luz_frontal') {
      return 'Franja de luz interior que ilumina el rostro. Con interruptor de pared va en un solo tono; con sensor puedes elegir la tira BiLED de tres tonos.';
    }
    if (key.indexOf('tipo_luz') !== -1 || label.indexOf('luz') !== -1) {
      return 'Selecciona el tono de iluminacion que mejor encaja con tu baño y tu rutina diaria.';
    }

    return 'Activa esta opcion para ver las variantes disponibles y su impacto en el producto.';
  }

  function isColorAccessoryField(field, groupLabel) {
    if (!field) return false;
    var key = String(field.key || '').toLowerCase();
    var label = String(field.label || '').toLowerCase();
    var group = String(groupLabel || '').toLowerCase();
    var options = Array.isArray(field.options) ? field.options : [];
    var colorOptionCount = options.filter(function (opt) {
      var optionLabel = String(opt && opt.label || '').toLowerCase();
      return optionLabel.indexOf('negro') !== -1 ||
        optionLabel.indexOf('black') !== -1 ||
        optionLabel.indexOf('blanco') !== -1 ||
        optionLabel.indexOf('white') !== -1 ||
        optionLabel.indexOf('dorado') !== -1 ||
        optionLabel.indexOf('oro') !== -1 ||
        optionLabel.indexOf('gold') !== -1 ||
        optionLabel.indexOf('plateado') !== -1 ||
        optionLabel.indexOf('plata') !== -1 ||
        optionLabel.indexOf('silver') !== -1 ||
        optionLabel.indexOf('cobre') !== -1 ||
        optionLabel.indexOf('bronce') !== -1;
    }).length;
    return (key.indexOf('color') !== -1 && key.indexOf('marco') !== -1) ||
      key === 'color' ||
      (label.indexOf('color') !== -1 && label.indexOf('marco') !== -1) ||
      label === 'color' ||
      (label === 'color' && group.indexOf('color del marco') !== -1) ||
      (group.indexOf('color del marco') !== -1) ||
      colorOptionCount >= 2;
  }

  function getAccessoryColorSwatch(option, optionInfo) {
    if (optionInfo && optionInfo.color) {
      return {
        style: getOptionColorStyle(optionInfo.color),
        check: '&#10003;'
      };
    }
    var label = String(option && option.label || '').toLowerCase();
    if (label.indexOf('negro') !== -1 || label.indexOf('black') !== -1) {
      return { style: 'background:#000;border-color:#000;color:#fff;', check: '&#10003;' };
    }
    if (label.indexOf('blanco') !== -1 || label.indexOf('white') !== -1) {
      return { style: 'background:#fff;border-color:#c9cfca;color:#2f3430;', check: '&#10003;' };
    }
    if (label.indexOf('dorado') !== -1 || label.indexOf('oro') !== -1 || label.indexOf('gold') !== -1) {
      return { style: 'background:#D4AF37;border-color:#D4AF37;color:#fff;', check: '&#10003;' };
    }
    if (label.indexOf('plateado') !== -1 || label.indexOf('silver') !== -1 || label.indexOf('plata') !== -1) {
      return { style: 'background:#E5E4E2;border-color:#cfcfcb;color:#2f3430;', check: '&#10003;' };
    }
    return { style: 'background:#f4f4f0;border-color:#cfd4ce;color:#2f3430;', check: '&#10003;' };
  }

  function getOptionColorStyle(color) {
    var colorValue = String(color || '').trim();
    if (!colorValue) return 'background:#f4f4f0;border-color:#cfd4ce;color:#2f3430;';
    return 'background:' + colorValue + ';border-color:' + getVisibleBorderForBackground(colorValue) + ';color:' + getReadableColorForBackground(colorValue) + ';';
  }

  function getVisibleBorderForBackground(color) {
    var luminance = getHexLuminance(color);
    if (luminance === null) return color;
    return luminance > 0.78 ? '#bfc6bf' : color;
  }

  function getReadableColorForBackground(color) {
    var luminance = getHexLuminance(color);
    if (luminance === null) return '#2f3430';
    return luminance > 0.58 ? '#2f3430' : '#fff';
  }

  function getHexLuminance(color) {
    var hex = String(color || '').trim().replace('#', '');
    if (hex.length === 3) {
      hex = hex.split('').map(function (char) { return char + char; }).join('');
    }
    if (!/^[0-9a-f]{6}$/i.test(hex)) return null;
    var r = parseInt(hex.slice(0, 2), 16);
    var g = parseInt(hex.slice(2, 4), 16);
    var b = parseInt(hex.slice(4, 6), 16);
    return (0.2126 * r + 0.7152 * g + 0.0722 * b) / 255;
  }

  function getAccessoryOptionIcon(field, option) {
    var fieldKey = String(field.key || '').toLowerCase();
    var label = String(option.label || '').toLowerCase();

    if (fieldKey.indexOf('encendido') === 0 || fieldKey === 'sensores') {
      var hasSensor = label.indexOf('sensor') !== -1;
      var hasSwitch = label.indexOf('interruptor') !== -1;
      if (label.indexOf('dos luces') !== -1 || label.indexOf('compartido') !== -1) {
        return '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8"><circle cx="12" cy="13" r="4"/><path d="M12 11.5v3" stroke-linecap="round"/><path d="M4 5.5h5M15 5.5h5M6.5 3.5v4M17.5 3.5v4" stroke-linecap="round"/></svg>';
      }
      if (label.indexOf('dos sensores') !== -1 || label.indexOf('por luz') !== -1) {
        return '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8"><circle cx="7" cy="12" r="4"/><circle cx="17" cy="12" r="4"/><path d="M7 10.5v3M17 10.5v3" stroke-linecap="round"/></svg>';
      }
      if (hasSensor && hasSwitch) {
        return '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8"><rect x="3" y="7" width="7" height="10" rx="1.5"/><rect x="5" y="9" width="3" height="3" rx="0.6" fill="currentColor" stroke="none"/><circle cx="17" cy="12" r="4"/><path d="M17 10.5v3" stroke-linecap="round"/></svg>';
      }
      if (hasSensor) {
        return '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8"><circle cx="12" cy="12" r="4"/><path d="M12 10.5v3" stroke-linecap="round"/><path d="M5.5 8.5a7.5 7.5 0 0 0 0 7M18.5 8.5a7.5 7.5 0 0 1 0 7" stroke-linecap="round"/></svg>';
      }
      return '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8"><rect x="8" y="4" width="8" height="16" rx="2"/><rect x="10" y="6" width="4" height="5" rx="1" fill="currentColor" stroke="none"/></svg>';
    }

    if (fieldKey.indexOf('sensor') !== -1 || label.indexOf('sensor') !== -1) {
      if (label.indexOf('táctil') !== -1 || label.indexOf('tactil') !== -1) {
        return '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8"><path d="M12 4v8m0 0V8.5a1.75 1.75 0 1 1 3.5 0V13m-3.5-1V7.75a1.75 1.75 0 1 0-3.5 0V14m3.5-2V6.75a1.75 1.75 0 1 1 3.5 0V13m0 0V9.75a1.75 1.75 0 1 1 3.5 0V15a6.5 6.5 0 1 1-13 0v-2.5" stroke-linecap="round" stroke-linejoin="round"/></svg>';
      }
      if (label.indexOf('infrarrojo') !== -1) {
        return '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8"><path d="M12 9.5a2.5 2.5 0 1 1 0 5 2.5 2.5 0 0 1 0-5Zm-5.5 2.5a5.5 5.5 0 0 1 11 0m-14 0a8.5 8.5 0 0 1 17 0m-14 0a8.5 8.5 0 0 0 17 0" stroke-linecap="round" stroke-linejoin="round"/></svg>';
      }
      if (label.indexOf('reloj') !== -1 || label.indexOf('temp') !== -1) {
        return '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8"><path d="M5 12h4m6 0h4M12 5v4m0 6v4" stroke-linecap="round"/><path d="M9 12a3 3 0 1 0 6 0 3 3 0 0 0-6 0Z" stroke-linecap="round" stroke-linejoin="round"/></svg>';
      }
    }

    if (fieldKey.indexOf('lamina') !== -1 || fieldKey.indexOf('antivaho') !== -1 || label.indexOf('antivaho') !== -1) {
      if (label.indexOf('sensor') !== -1) {
        return '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8"><path d="M4 14c2.5 0 2.5-4 5-4s2.5 4 5 4m-10 4c2.5 0 2.5-4 5-4s2.5 4 5 4" stroke-linecap="round"/><path d="M18 8.5a3.5 3.5 0 0 1 0 7m2.2-9.2a6 6 0 0 1 0 11.4" stroke-linecap="round"/></svg>';
      }
      return '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8"><path d="M4 14c2.5 0 2.5-4 5-4s2.5 4 5 4m-10 4c2.5 0 2.5-4 5-4s2.5 4 5 4" stroke-linecap="round"/><path d="M17 4.5c2 1.2 3 3.1 3 5.5" stroke-linecap="round"/></svg>';
    }

    if (fieldKey.indexOf('altavoz') !== -1 || label.indexOf('bluetooth') !== -1) {
      return '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8"><path d="m10 7 4 5-4 5m4-10-4 5 4 5" stroke-linecap="round" stroke-linejoin="round"/><path d="M6.5 9.5 11 12l-4.5 2.5M17.5 9.5 13 12l4.5 2.5" stroke-linecap="round" stroke-linejoin="round"/></svg>';
    }

    if (fieldKey.indexOf('digital') !== -1) {
      return '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8"><rect x="5" y="6" width="14" height="12" rx="2"/><path d="M8 10h2m4 0h2m-8 4h8" stroke-linecap="round"/></svg>';
    }

    if (fieldKey === 'tipo_luz_ambiental' || fieldKey === 'tipo_luz_frontal') {
      if (label.indexOf('biled') !== -1) {
        return '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8"><circle cx="5.5" cy="12" r="2.5"/><circle cx="12" cy="12" r="2.5"/><circle cx="18.5" cy="12" r="2.5"/><path d="M12 4.5v2m0 11v2" stroke-linecap="round"/></svg>';
      }
      return '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8"><circle cx="12" cy="12" r="3.5"/><path d="M12 3v2.5M12 18.5V21M3 12h2.5M18.5 12H21M5.6 5.6l1.8 1.8M16.6 16.6l1.8 1.8M5.6 18.4l1.8-1.8M16.6 7.4l1.8-1.8" stroke-linecap="round"/></svg>';
    }

    if (fieldKey.indexOf('aumento') !== -1) {
      return '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8"><circle cx="11" cy="11" r="5"/><path d="m15 15 4 4M11 9v4M9 11h4" stroke-linecap="round"/></svg>';
    }

    return '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6"><path d="M5 18h14M8 18l1.5-5h5L16 18M9 13V6h6v7M10.5 8.5h3" stroke-linecap="round" stroke-linejoin="round"/></svg>';
  }

  // Varias opciones de un mismo campo pueden compartir valor (p. ej. los tonos
  // de luz, todos "0"), y las reglas de visibilidad separan <option>s del DOM,
  // asi que ni el valor ni selectedIndex identifican la opcion elegida. Se usa
  // el indice original (data-index) que llevan las <option> y las tarjetas.
  function getSelectedOptionIndex(select) {
    var opt = select.options[select.selectedIndex];
    if (opt && opt.hasAttribute('data-index')) return String(opt.getAttribute('data-index'));
    return select.selectedIndex >= 0 ? String(select.selectedIndex) : '';
  }

  function selectOptionByIndex(select, index) {
    var opts = Array.prototype.slice.call(select.options);
    var target = opts.find(function (o) { return String(o.getAttribute('data-index')) === String(index); });
    if (!target) return false;
    select.selectedIndex = opts.indexOf(target);
    return true;
  }

  function syncAccessoryButtons(fieldWrap, select) {
    var idx = getSelectedOptionIndex(select);
    var value = String(select.value);
    var buttons = Array.prototype.slice.call(fieldWrap.querySelectorAll('[data-accessory-option]'));
    var winner = buttons.find(function (b) { return String(b.getAttribute('data-index')) === idx; });
    if (!winner) {
      winner = buttons.find(function (b) { return String(b.getAttribute('data-value')) === value; }) || null;
    }
    buttons.forEach(function (b) { b.classList.toggle('is-selected', b === winner); });
  }

  function initAccessoryControls(wrapper) {
    wrapper.querySelectorAll('[data-accessory-field]').forEach(function (fieldEl) {
      var select = fieldEl.querySelector('select[data-config-key]');
      var toggle = fieldEl.querySelector('[data-accessory-toggle]');
      var optionsWrap = fieldEl.querySelector('[data-accessory-options]');
      var buttons = fieldEl.querySelectorAll('[data-accessory-option]');
      if (!select || !toggle || !optionsWrap) return;

      function selectedIsOff() {
        var opt = select.options[select.selectedIndex];
        return opt && isOffOption({ label: opt.text, value: opt.value });
      }

      function firstActiveValue() {
        for (var i = 0; i < select.options.length; i += 1) {
          if (!isOffOption({ label: select.options[i].text, value: select.options[i].value })) {
            return i;
          }
        }
        return select.selectedIndex;
      }

      function sync() {
        var active = !selectedIsOff();
        fieldEl.classList.toggle('is-active', active);
        toggle.checked = active;
        optionsWrap.hidden = !active;
        syncAccessoryButtons(fieldEl, select);
      }

      toggle.addEventListener('change', function () {
        if (toggle.checked) {
          select.selectedIndex = firstActiveValue();
        } else {
          var offIndex = Array.prototype.findIndex.call(select.options, function (option) {
            return isOffOption({ label: option.text, value: option.value });
          });
          if (offIndex !== -1) select.selectedIndex = offIndex;
        }
        select.dispatchEvent(new Event('change', { bubbles: true }));
      });

      buttons.forEach(function (button) {
        button.addEventListener('click', function () {
          // Pre-mark clicked button so sync() preserves it for duplicate values.
          buttons.forEach(function (b) { b.classList.remove('is-selected'); });
          button.classList.add('is-selected');
          if (!selectOptionByIndex(select, button.dataset.index)) {
            select.selectedIndex = Number(button.dataset.index);
          }
          select.dispatchEvent(new Event('change', { bubbles: true }));
        });
      });

      select.addEventListener('change', sync);
      sync();
    });
  }

  function initFieldSwatches(wrapper) {
    wrapper.querySelectorAll('.configurator-field--swatch-select').forEach(function (fieldEl) {
      var select = fieldEl.querySelector('select[data-config-key]');
      var buttons = fieldEl.querySelectorAll('[data-field-swatch]');
      if (!select || !buttons.length) return;

      function sync() {
        buttons.forEach(function (button) {
          button.classList.toggle('is-selected', Number(button.dataset.index) === select.selectedIndex);
        });
      }

      buttons.forEach(function (button) {
        button.addEventListener('click', function () {
          select.selectedIndex = Number(button.dataset.index);
          select.dispatchEvent(new Event('change', { bubbles: true }));
          sync();
        });
      });

      select.addEventListener('change', sync);
      sync();
    });
  }

  function initRalControls(wrapper) {
    var panel = wrapper.querySelector('[data-ral-panel]');
    if (!panel) return;

    var select = wrapper.querySelector('select[data-config-key="color"]');
    var input = panel.querySelector('[data-ral-input]');
    var error = panel.querySelector('[data-ral-error]');
    if (!select || !input) return;

    function sync() {
      var active = isRalSelected(select);
      panel.hidden = !active;
      if (!active) {
        input.value = '';
        input.classList.remove('is-invalid');
        if (error) error.hidden = true;
      }
    }

    select.addEventListener('change', sync);
    input.addEventListener('input', function () {
      input.value = normalizeRalCode(input.value);
      input.classList.remove('is-invalid');
      if (error) error.hidden = true;
    });

    sync();
  }

  function isRalSelected(select) {
    if (!select || select.selectedIndex < 0 || !select.options[select.selectedIndex]) return false;
    var option = select.options[select.selectedIndex];
    var label = normalizeOptionLabel(option.text || option.label || '');
    return label.indexOf('paleta ral') !== -1 || label.indexOf(' ral') !== -1 || label === 'ral';
  }

  function normalizeRalCode(value) {
    return String(value || '')
      .toUpperCase()
      .replace(/[^A-Z0-9\s-]/g, '')
      .replace(/\s+/g, ' ')
      .trim()
      .slice(0, 12);
  }

  function getRalCode(form) {
    var input = form.querySelector('[data-ral-input]');
    return input ? normalizeRalCode(input.value) : '';
  }

  function validateRalSelection(form) {
    var colorSelect = form.querySelector('select[data-config-key="color"]');
    var input = form.querySelector('[data-ral-input]');
    var error = form.querySelector('[data-ral-error]');
    var requiresRal = isRalSelected(colorSelect);
    var code = getRalCode(form);

    if (!requiresRal || code) {
      if (input) input.classList.remove('is-invalid');
      if (error) error.hidden = true;
      return { ok: true, message: '' };
    }

    if (input) {
      input.classList.add('is-invalid');
      input.focus();
    }
    if (error) error.hidden = false;
    return { ok: false, message: 'Introduce el código RAL antes de añadir el espejo al carrito.' };
  }

  function initFieldTiles(wrapper) {
    wrapper.querySelectorAll('.configurator-field--tile-select').forEach(function (fieldEl) {
      var select = fieldEl.querySelector('select[data-config-key]');
      var buttons = fieldEl.querySelectorAll('[data-field-tile]');
      var current = fieldEl.querySelector('[data-field-current]');
      if (!select || !buttons.length) return;

      function sync() {
        buttons.forEach(function (button) {
          button.classList.toggle('is-selected', Number(button.dataset.index) === select.selectedIndex);
        });
        if (current && select.options[select.selectedIndex]) {
          current.textContent = select.options[select.selectedIndex].text;
        }
      }

      buttons.forEach(function (button) {
        button.addEventListener('click', function () {
          select.selectedIndex = Number(button.dataset.index);
          select.dispatchEvent(new Event('change', { bubbles: true }));
          sync();
        });
      });

      select.addEventListener('change', sync);
      sync();
    });
  }

  // ---------------------------------------------------------------------------
  // Condition evaluation — show/hide fields based on current selections
  // ---------------------------------------------------------------------------

  /**
   * Evaluate a single 'when' clause against the current selection map.
   * Supported ops: eq, neq, lt, gt, lte, gte
   */
  function evalClause(clause, selected) {
    var sel = selected[clause.field];
    if (sel === undefined || sel === null) return false;

    var selNum = parseFloat(sel);
    var valNum = parseFloat(clause.value);

    switch (clause.op) {
      case 'eq':  return sel === clause.value || (!isNaN(selNum) && !isNaN(valNum) && selNum === valNum);
      case 'neq': return sel !== clause.value && (isNaN(selNum) || isNaN(valNum) || selNum !== valNum);
      case 'lt':  return !isNaN(selNum) && !isNaN(valNum) && selNum < valNum;
      case 'gt':  return !isNaN(selNum) && !isNaN(valNum) && selNum > valNum;
      case 'lte': return !isNaN(selNum) && !isNaN(valNum) && selNum <= valNum;
      case 'gte': return !isNaN(selNum) && !isNaN(valNum) && selNum >= valNum;
      default:    return false;
    }
  }

  /**
   * Apply all visibility conditions from config.conditions.
   * Called after every field change and on initial render.
   *
   * Each condition:
   *   { when: [{field, op, value}, …], logic: "or"|"and", action: "hide"|"show", target: "field_key" }
   *
   * "hide" action: if condition is TRUE → hide the field wrapper + reset its select to default.
   * Multiple conditions may target the same field; if ANY hides it, it stays hidden.
   */
  function applyConditions(wrapper, config) {
    var conditions = config.conditions;
    if (!conditions || !conditions.length) return;

    var selected = {};
    wrapper.querySelectorAll('select[data-config-key]').forEach(function (sel) {
      selected[sel.getAttribute('data-config-key')] = sel.value;
    });
    wrapper.querySelectorAll('input[type="checkbox"][data-config-key]').forEach(function (cb) {
      selected[cb.getAttribute('data-config-key')] = cb.checked ? '1' : '0';
    });

    // Collect hide verdicts per target field key (true = must be hidden)
    var hideMap = {};

    conditions.forEach(function (cond) {
      var clauses = cond.when;
      var logic   = cond.logic || 'or';
      var matched;

      if (logic === 'and') {
        matched = clauses.every(function (c) { return evalClause(c, selected); });
      } else {
        matched = clauses.some(function (c) { return evalClause(c, selected); });
      }

      var target = cond.target;
      if (cond.action === 'hide') {
        // Once any condition marks a field hidden, it stays hidden
        if (matched) hideMap[target] = true;
        else if (hideMap[target] === undefined) hideMap[target] = false;
      }
      // "show" actions (rare) could be added here in the future
    });

    // Apply hide/show to each target field's DOM wrapper
    Object.keys(hideMap).forEach(function (fieldKey) {
      var el = wrapper.querySelector('[data-config-key="' + fieldKey + '"]');
      if (!el) return;
      var fieldWrap = el.closest('.configurator-field');
      if (!fieldWrap) return;

      if (hideMap[fieldKey]) {
        fieldWrap.style.display = 'none';
        // Reset the hidden select to its first option (value "0" or first option)
        // so it doesn't silently add price/properties to the cart
        if (el.tagName === 'SELECT') {
          el.value = el.options[0] ? el.options[0].value : '0';
        } else if (el.type === 'checkbox') {
          el.checked = false;
        }
      } else {
        fieldWrap.style.display = '';
      }
    });
  }

  // ---------------------------------------------------------------------------
  // Visibility rules (FASE 3+ — replica del PS DP module via dp-evaluator.js)
  //
  // config.visibility_rules: [{ when: "[campo] = valor", hide_fields: [...],
  //                              hide_options: [{field, value}] }]
  // Si el metafield no trae visibility_rules o DPEvaluator no está cargado,
  // esta función es no-op (productos no migrados siguen funcionando con
  // applyConditions legacy).
  // ---------------------------------------------------------------------------
  // Predefined-sizes products store the size as a single string "WxH" in `medida`
  // but PS DP intervals reference [ancho] and [alto] separately. Derive them.
  function enrichStateWithSize(state, config) {
    if (!config) return state;
    if (config.type === 'predefined_sizes') {
      var medida = state.medida || state.medidas || '';
      var m = String(medida).match(/^(\d+)\s*[xX×]\s*(\d+)/);
      if (m) {
        if (state.ancho === undefined) state.ancho = m[1];
        if (state.alto  === undefined) state.alto  = m[2];
      }
    } else if (config.type === 'circular') {
      // Some intervals on circular products may reference [ancho] as a synonym
      // for [diametro]. Mirror it.
      if (state.diametro && state.ancho === undefined) state.ancho = state.diametro;
    }
    return state;
  }

  // ---------------------------------------------------------------------------
  // Campos bloqueados que copian la opcion de otro campo (regla lock_fields del
  // metafield). Ej. THEMIS: con un solo interruptor o un solo sensor las dos
  // luces se encienden a la vez, asi que el tono frontal copia al ambiental.
  // ---------------------------------------------------------------------------
  var MIRROR_LOCK_NOTE = 'Misma opci\u00f3n que el campo anterior';

  function syncFieldButtonsByIndex(fieldWrap, select) {
    if (fieldWrap.hasAttribute('data-accessory-field')) {
      syncAccessoryButtons(fieldWrap, select);
      return;
    }
    var idx = getSelectedOptionIndex(select);
    fieldWrap.querySelectorAll('[data-field-tile], [data-field-swatch]').forEach(function (b) {
      b.classList.toggle('is-selected', String(b.getAttribute('data-index')) === idx);
    });
    var current = fieldWrap.querySelector('[data-field-current]');
    if (current && select.options[select.selectedIndex]) {
      current.textContent = select.options[select.selectedIndex].text;
    }
  }

  function lockMirroredField(fieldWrap, select, note) {
    syncFieldButtonsByIndex(fieldWrap, select);
    fieldWrap.classList.add('is-mirrored');
    fieldWrap.setAttribute('data-vis-rule-mirror', '1');
    fieldWrap.querySelectorAll('[data-accessory-option], [data-field-tile], [data-field-swatch]').forEach(function (btn) {
      if (btn.disabled) return; // ya deshabilitado por otra regla
      btn.disabled = true;
      btn.setAttribute('aria-disabled', 'true');
      btn.setAttribute('data-vis-rule-mirror-btn', '1');
    });
    var noteEl = fieldWrap.querySelector('[data-mirror-note]');
    if (!noteEl) {
      noteEl = document.createElement('p');
      noteEl.className = 'configurator-mirror-note';
      noteEl.setAttribute('data-mirror-note', '');
      noteEl.innerHTML =
        '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" aria-hidden="true">' +
          '<rect x="5" y="11" width="14" height="10" rx="2"/>' +
          '<path d="M8 11V7.5a4 4 0 0 1 8 0V11" stroke-linecap="round"/>' +
        '</svg><span></span>';
      // Encima de las tarjetas, junto a lo que queda bloqueado
      var optionsHost = fieldWrap.querySelector('[data-accessory-options]');
      if (optionsHost) {
        optionsHost.insertBefore(noteEl, optionsHost.firstChild);
      } else {
        var host = fieldWrap.querySelector('.configurator-accessory-copy')
          || fieldWrap.querySelector('.configurator-field__surface')
          || fieldWrap;
        host.appendChild(noteEl);
      }
    }
    noteEl.querySelector('span').textContent = note || MIRROR_LOCK_NOTE;
  }

  function unlockMirroredField(fieldWrap) {
    fieldWrap.classList.remove('is-mirrored');
    fieldWrap.removeAttribute('data-vis-rule-mirror');
    fieldWrap.querySelectorAll('[data-vis-rule-mirror-btn="1"]').forEach(function (btn) {
      btn.disabled = false;
      btn.removeAttribute('aria-disabled');
      btn.removeAttribute('data-vis-rule-mirror-btn');
    });
    var noteEl = fieldWrap.querySelector('[data-mirror-note]');
    if (noteEl && noteEl.parentNode) noteEl.parentNode.removeChild(noteEl);
  }

  function collectVisibilityState(root, config) {
    var state = {};
    root.querySelectorAll('select[data-config-key]').forEach(function (sel) {
      state[sel.getAttribute('data-config-key')] = sel.value;
    });
    root.querySelectorAll('input[type="checkbox"][data-config-key]').forEach(function (cb) {
      state[cb.getAttribute('data-config-key')] = cb.checked ? '1' : '0';
    });
    root.querySelectorAll('input[type="number"][data-config-key], input[type="range"][data-config-key]').forEach(function (inp) {
      state[inp.getAttribute('data-config-key')] = inp.value;
    });
    return enrichStateWithSize(state, config);
  }

  function applyVisibilityRules(wrapper, config, isRepass) {
    if (!config.visibility_rules || !window.DPEvaluator) return;

    // Collect state from current selections
    var state = collectVisibilityState(wrapper, config);

    var vis = window.DPEvaluator.applyVisibility(
      config.visibility_rules, state, config.base_prices, config.intervals
    );

    // 1. Reset previous markers from this function. For <option> elements we
    //    physically detach/reattach to be reliable across browsers (style.display
    //    on <option> is not universally honored by Safari/Firefox).
    //
    //    Restore detached options (stored in select's _detachedOptions) first.
    //    Una opcion separada conserva el "selected" de cuando estaba dentro y, al
    //    volver a insertarla, Chrome la da por elegida y pisa la eleccion actual
    //    (en el Segovia el alto volvia siempre a 45 y saltaba a 50). Se guarda la
    //    opcion elegida (no el valor: hay opciones con el mismo valor) y se repone.
    wrapper.querySelectorAll('select[data-config-key]').forEach(function (sel) {
      if (sel._detachedOptions && sel._detachedOptions.length) {
        var keepOption = sel.options[sel.selectedIndex] || null;
        sel._detachedOptions.forEach(function (item) {
          item.option.selected = false;
          // Re-insert at original index, clamped
          var idx = Math.min(item.index, sel.options.length);
          if (idx >= sel.options.length) sel.appendChild(item.option);
          else sel.insertBefore(item.option, sel.options[idx]);
          item.option.removeAttribute('data-vis-rule-hidden');
        });
        sel._detachedOptions = [];
        if (keepOption) keepOption.selected = true;
      }
    });
    // Reset accessory-toggle lock applied by previous pass
    wrapper.querySelectorAll('[data-accessory-field][data-vis-rule-locked="1"]').forEach(function (fieldEl) {
      var toggleInput = fieldEl.querySelector('.configurator-accessory-toggle__input');
      if (toggleInput) {
        toggleInput.disabled = false;
        toggleInput.removeAttribute('aria-disabled');
      }
      fieldEl.classList.remove('is-required');
      fieldEl.removeAttribute('data-vis-rule-locked');
    });
    // Reset mirror locks (lock_fields) applied by previous pass
    wrapper.querySelectorAll('[data-vis-rule-mirror="1"]').forEach(function (fieldEl) {
      unlockMirroredField(fieldEl);
    });
    wrapper.querySelectorAll('[data-vis-rule-hidden="1"]').forEach(function (el) {
      if (el.tagName === 'BUTTON') {
        el.disabled = false;
        el.removeAttribute('aria-disabled');
        el.style.opacity = '';
        el.style.pointerEvents = '';
        el.style.filter = '';
      } else if (el.tagName !== 'OPTION') {
        // Field wrap
        el.style.display = '';
      }
      el.removeAttribute('data-vis-rule-hidden');
    });

    // 2. Hide whole fields
    vis.hiddenFields.forEach(function (key) {
      var el = wrapper.querySelector('[data-config-key="' + key + '"]');
      if (!el) return;
      var fieldWrap = el.closest('.configurator-field');
      if (!fieldWrap) return;
      fieldWrap.style.display = 'none';
      fieldWrap.setAttribute('data-vis-rule-hidden', '1');
      // Reset value so it doesn't silently affect price/cart
      if (el.tagName === 'SELECT' && el.options[0]) el.value = el.options[0].value;
      else if (el.type === 'checkbox') el.checked = false;
    });

    // Si una regla quita la opcion elegida y el campo pasa a otra, las reglas que
    // dependian de la opcion anterior ya no valen: se hace una pasada mas (abajo).
    // Ej. LUXE: detector de proximidad (oculta los tonos fijos) en una medida que
    // no lo admite (oculta la luz regulable) dejaba el campo de luz sin opciones.
    var forcedChange = false;

    // 3. Hide individual dropdown options + auto-reset if selected became hidden.
    //    Also visually disable card-style buttons (data-field-tile, data-field-swatch,
    //    data-accessory-option) so the user gets feedback that they can't pick them.
    Object.keys(vis.hiddenOptions).forEach(function (fieldKey) {
      var sel = wrapper.querySelector('select[data-config-key="' + fieldKey + '"]');
      if (!sel) return;
      var hiddenSet = vis.hiddenOptions[fieldKey];
      var fieldWrap = sel.closest('.configurator-field');

      // Si la regla va a quitar la opcion que el cliente tenia elegida, se
      // recuerda para devolversela cuando la regla deje de aplicar (paso 5).
      var chosenBefore = sel.options[sel.selectedIndex] || null;
      var chosenHidden = !!chosenBefore && hiddenSet.has(String(chosenBefore.value));

      // Detach native <option>s physically (cross-browser safe).
      if (!sel._detachedOptions) sel._detachedOptions = [];
      var optsList = Array.prototype.slice.call(sel.options);
      optsList.forEach(function (opt, idx) {
        if (hiddenSet.has(String(opt.value))) {
          opt.setAttribute('data-vis-rule-hidden', '1');
          sel._detachedOptions.push({ index: idx, option: opt });
          if (opt.parentNode) opt.parentNode.removeChild(opt);
        }
      });

      // Disable card-style buttons that share the same value
      if (fieldWrap) {
        var btnSel = '[data-field-tile][data-value], [data-field-swatch][data-value], [data-accessory-option][data-value]';
        fieldWrap.querySelectorAll(btnSel).forEach(function (btn) {
          if (hiddenSet.has(String(btn.getAttribute('data-value')))) {
            btn.disabled = true;
            btn.setAttribute('aria-disabled', 'true');
            btn.style.opacity = '0.4';
            btn.style.pointerEvents = 'none';
            btn.style.filter = 'grayscale(50%)';
            btn.setAttribute('data-vis-rule-hidden', '1');
          }
        });
      }

      // Auto-reset if currently-selected value was hidden
      if (hiddenSet.has(String(sel.value))) {
        forcedChange = true;
        var firstVisible = Array.prototype.slice.call(sel.options).find(function (o) {
          return !o.disabled;
        });
        if (firstVisible) {
          sel.value = firstVisible.value;
          // Sync card-button selection state
          if (fieldWrap) {
            fieldWrap.querySelectorAll('[data-field-tile], [data-field-swatch]').forEach(function (b) {
              var matches = String(b.getAttribute('data-value')) === String(firstVisible.value);
              b.classList.toggle('is-selected', matches);
            });
            if (fieldWrap.hasAttribute('data-accessory-field')) syncAccessoryButtons(fieldWrap, sel);
          }
        }
        // No dispatchEvent — caller will run updatePrice next, picking up the new state.
      }

      if (chosenHidden) {
        // Al separar la opcion elegida el navegador ya marca otra, asi que el
        // cambio se detecta aqui y no en el auto-reset de arriba.
        forcedChange = true;
        if (!sel._ruleRestore) sel._ruleRestore = chosenBefore;
        sel._ruleForced = sel.options[sel.selectedIndex] || null;
      }

      // Sync accessory-field UI: when the rule forced a non-off value, the
      // accessory's own change-listener is not fired (we deliberately skip
      // dispatchEvent above to avoid a feedback loop). Mirror sync() manually so
      // the toggle, fila visible y botón seleccionado reflejan el valor real.
      // Además, si la opción "off" del campo está oculta por reglas, bloquea el
      // toggle (no se puede apagar) y marca la fila como requerida.
      if (fieldWrap && fieldWrap.hasAttribute('data-accessory-field')) {
        var accSelected = sel.options[sel.selectedIndex];
        var accIsOff = accSelected && isOffOption({ label: accSelected.text, value: accSelected.value });
        var accToggle = fieldWrap.querySelector('.configurator-accessory-toggle__input');
        var accOptionsWrap = fieldWrap.querySelector('[data-accessory-options]');
        var offHidden = Array.prototype.some.call(sel.options, function (o) {
          return isOffOption({ label: o.text, value: o.value }) && hiddenSet.has(String(o.value));
        });

        fieldWrap.classList.toggle('is-active', !accIsOff);
        if (accToggle) accToggle.checked = !accIsOff;
        if (accOptionsWrap) accOptionsWrap.hidden = accIsOff;
        syncAccessoryButtons(fieldWrap, sel);

        if (offHidden && accToggle) {
          accToggle.disabled = true;
          accToggle.setAttribute('aria-disabled', 'true');
          fieldWrap.classList.add('is-required');
          fieldWrap.setAttribute('data-vis-rule-locked', '1');
        }
      }
    });

    // 4. Campos bloqueados que copian la opcion elegida en otro campo (lock_fields).
    //    Va despues de ocultar opciones para que el indice copiado exista en el
    //    campo destino; si no existe, el campo se deja libre.
    Object.keys(vis.lockedFields || {}).forEach(function (fieldKey) {
      var lock = vis.lockedFields[fieldKey];
      var tgt = wrapper.querySelector('select[data-config-key="' + fieldKey + '"]');
      var src = wrapper.querySelector('select[data-config-key="' + lock.mirror + '"]');
      if (!tgt || !src) return;
      var tgtWrap = tgt.closest('.configurator-field');
      if (!tgtWrap || tgtWrap.getAttribute('data-vis-rule-hidden') === '1') return;
      if (!selectOptionByIndex(tgt, getSelectedOptionIndex(src))) return;
      lockMirroredField(tgtWrap, tgt, lock.note);
    });

    // 5. Devuelve la opcion que el cliente tenia antes de que una regla se la
    //    quitara, cuando la regla ya no aplica y el cliente no ha elegido otra.
    //    Sin esto, al elegir "3 colores" (que exige sensor) y volver a un tono
    //    fijo, el sensor se quedaba activado y se cobraba sin haberlo pedido.
    var restored = false;
    wrapper.querySelectorAll('select[data-config-key]').forEach(function (sel) {
      var back = sel._ruleRestore;
      if (!back) return;
      var current = sel.options[sel.selectedIndex] || null;
      if (current !== sel._ruleForced) {
        // el cliente ha elegido otra opcion: manda la suya
        sel._ruleRestore = null;
        sel._ruleForced = null;
        return;
      }
      if (!sel.contains(back)) return; // la regla sigue aplicando

      back.selected = true;
      sel._ruleRestore = null;
      sel._ruleForced = null;
      restored = true;

      var fieldWrap = sel.closest('.configurator-field');
      if (!fieldWrap) return;
      fieldWrap.querySelectorAll('[data-field-tile], [data-field-swatch]').forEach(function (b) {
        b.classList.toggle('is-selected', String(b.getAttribute('data-value')) === String(sel.value));
      });
      if (fieldWrap.hasAttribute('data-accessory-field')) {
        var isOff = isOffOption({ label: back.text, value: back.value });
        var toggle = fieldWrap.querySelector('.configurator-accessory-toggle__input');
        var optionsWrap = fieldWrap.querySelector('[data-accessory-options]');
        fieldWrap.classList.toggle('is-active', !isOff);
        if (toggle) toggle.checked = !isOff;
        if (optionsWrap) optionsWrap.hidden = isOff;
        syncAccessoryButtons(fieldWrap, sel);
      }
    });
    // La opcion devuelta puede activar o desactivar otras reglas: una pasada mas.
    // Hasta tres pasadas: devolver una opcion puede soltar otra regla (DIAN LUXE: del
    // detector a Ø45 cambia el sensor y despues vuelve el tono fijo).
    var passDepth = isRepass === true ? 1 : (isRepass || 0);
    if ((restored || forcedChange) && passDepth < 3) applyVisibilityRules(wrapper, config, passDepth + 1);
  }


  // ---------------------------------------------------------------------------
  // Price calculation
  // ---------------------------------------------------------------------------
  function getSelectedValues(form) {
    var values = {};
    var selects = form.querySelectorAll('select[data-config-key]');
    selects.forEach(function (sel) {
      values[sel.getAttribute('data-config-key')] = sel.value;
    });
    var colorSelect = form.querySelector('select[data-config-key="color"]');
    var ralCode = getRalCode(form);
    if (isRalSelected(colorSelect) && ralCode) {
      values.color_ral = ralCode;
    }
    return values;
  }

  function parseMeasureNumber(value) {
    if (value === null || value === undefined) return 0;
    var match = String(value).replace(',', '.').match(/(\d+(?:\.\d+)?)/);
    return match ? parseFloat(match[1]) : 0;
  }

  function getPreviewDimensions(selected, config) {
    var type = config.type;
    var width = 0;
    var height = 0;
    var shape = 'rect';
    var label = '';

    if (type === 'rectangular') {
      width = parseMeasureNumber(selected.ancho || selected.ancho1);
      height = parseMeasureNumber(selected.alto || selected.alto1);
      label = width && height ? Math.round(width) + ' x ' + Math.round(height) + ' cm' : '';
      // Productos a medida con forma no rectangular (p. ej. cápsula/ovalado):
      // el metafield puede indicar preview_shape: "oval" para la previsualización.
      // "organic" para formas libres (WIN LUXE): curvas suaves que no dan un
      // circulo cuando ancho y alto coinciden.
      shape = config.preview_shape === 'oval' ? 'oval' : (config.preview_shape === 'organic' ? 'organic' : 'rect');
      return { width: width, height: height, label: label, shape: shape };
    }

    if (type === 'circular') {
      width = parseMeasureNumber(selected.diametro);
      label = width ? '\u00d8 ' + Math.round(width) + ' cm' : '';
      return { width: width, height: width, label: label, shape: 'circle' };
    }

    if (type === 'predefined_sizes') {
      var sizeKey = selected.medida || selected.medidas || '';
      var nums = parsePredefinedSizeNumbers(sizeKey);
      if (nums.length >= 2) {
        width = parseMeasureNumber(nums[0]);
        height = parseMeasureNumber(nums[1]);
        // Medidas fijas de un modelo que no es ovalado: el metafield lo indica con
        // preview_shape "rect" o "circle" (los diámetros se guardan como "60x60").
        if (config.preview_shape === 'circle' && width === height) {
          shape = 'circle';
          label = 'Ø ' + Math.round(width) + ' cm';
        } else {
          shape = config.preview_shape === 'rect' ? 'rect' : (config.preview_shape === 'organic' ? 'organic' : 'oval');
          label = Math.round(width) + ' x ' + Math.round(height) + ' cm';
        }
      } else if (nums.length === 1) {
        width = parseMeasureNumber(nums[0]);
        height = width;
        shape = 'circle';
        label = '\u00d8 ' + Math.round(width) + ' cm';
      }
      return { width: width, height: height, label: label, shape: shape, controlLabel: label };
    }

    return { width: width, height: height, label: label, shape: shape };
  }

  function parsePredefinedSizeNumbers(sizeKey) {
    var raw = String(sizeKey || '').trim();
    var nums = raw.match(/\d+(?:[.,]\d+)?/g) || [];
    if (nums.length !== 1) return nums;

    var compact = nums[0].replace(/\D/g, '');
    if (!/^\d{4,5}$/.test(compact)) return nums;

    var candidates = [];
    if (compact.length >= 4) candidates.push([compact.slice(0, 2), compact.slice(2)]);
    if (compact.length === 5) candidates.push([compact.slice(0, 3), compact.slice(3)]);

    var valid = candidates.find(function (pair) {
      var width = parseInt(pair[0], 10);
      var height = parseInt(pair[1], 10);
      return width >= 30 && width <= 220 && height >= 30 && height <= 220;
    });

    return valid || nums;
  }

  function findMeasureSelect(form, keys) {
    for (var i = 0; i < keys.length; i += 1) {
      var select = form.querySelector('select[data-config-key="' + keys[i] + '"]');
      if (select) return select;
    }
    return null;
  }

  function getMeasureMeta(select) {
    if (!select) return null;
    var options = Array.prototype.slice.call(select.options || []).map(function (option, index) {
      return {
        index: index,
        value: option.value,
        label: option.text
      };
    }).filter(function (option) {
      return option.value !== '';
    });
    return options.length ? options : null;
  }

  function getPredefinedSizeOptions(select) {
    if (!select) return [];
    return Array.prototype.slice.call(select.options || []).map(function (option, index) {
      var nums = parsePredefinedSizeNumbers(option.value || option.text);
      if (nums.length < 2) nums = parsePredefinedSizeNumbers(option.text || option.value);
      if (nums.length < 2) return null;
      return {
        index: index,
        value: option.value,
        label: option.text,
        width: parseMeasureNumber(nums[0]),
        height: parseMeasureNumber(nums[1])
      };
    }).filter(function (option) {
      return option && option.width > 0 && option.height > 0;
    });
  }

  function uniqueSortedNumbers(values) {
    var seen = {};
    return values.filter(function (value) {
      var key = String(value);
      if (seen[key]) return false;
      seen[key] = true;
      return true;
    }).sort(function (a, b) { return a - b; });
  }

  function getNearestIndex(values, value) {
    if (!values.length) return 0;
    var nearest = 0;
    var nearestDistance = Infinity;
    values.forEach(function (candidate, index) {
      var distance = Math.abs(candidate - value);
      if (distance < nearestDistance) {
        nearest = index;
        nearestDistance = distance;
      }
    });
    return nearest;
  }

  function buildNumericTickMarkup(values, reverseOrder) {
    if (!values.length) return '';
    var labels = values.map(function (value) { return Math.round(value) + ' cm'; });
    if (reverseOrder) labels.reverse();
    return labels.map(function (label) {
      return '<span>' + escapeHtml(label) + '</span>';
    }).join('');
  }

  function buildTickMarkup(options, reverseOrder, formatter) {
    if (!options || !options.length) return '';

    var desiredIndexes = [0, Math.floor((options.length - 1) / 2), options.length - 1];
    if (options.length >= 4) {
      desiredIndexes = [0, Math.floor((options.length - 1) / 3), Math.floor((options.length - 1) * 2 / 3), options.length - 1];
    }

    var seen = {};
    var labels = desiredIndexes.map(function (idx) { return options[idx]; }).filter(function (option) {
      if (!option) return false;
      if (seen[option.index]) return false;
      seen[option.index] = true;
      return true;
    });

    if (reverseOrder) labels.reverse();

    return labels.map(function (option) {
      var label = typeof formatter === 'function' ? formatter(option) : option.label;
      return '<span>' + escapeHtml(label) + '</span>';
    }).join('');
  }

  // Las posiciones del deslizador son siempre la lista completa de medidas,
  // aunque las reglas de visibilidad saquen opciones del <select>: si no, el
  // indice del deslizador deja de casar con las marcas y con el valor elegido.
  // Una posicion que no se puede elegir (opcion oculta, o que dejaria oculta la
  // otra medida, como el ancho del Segovia al llegar a su alto) hace de tope:
  // el deslizador se queda en la ultima medida valida en lugar de cambiar la otra.
  function setupMeasureSlider(control, select, reverseTicks, tickFormatter, guard) {
    if (!control || !select) {
      if (control) control.hidden = true;
      return;
    }

    var slider = control.querySelector('input[type="range"]');
    var ticks = control.querySelector('[data-preview-height-ticks], [data-preview-width-ticks]');
    var options = getMeasureMeta(select);
    if (!slider || !options) {
      control.hidden = true;
      return;
    }

    control.hidden = false;
    slider.min = 0;
    slider.max = String(options.length - 1);
    slider.step = 1;

    if (ticks) ticks.innerHTML = buildTickMarkup(options, reverseTicks, tickFormatter);

    function indexOfValue(value) {
      for (var i = 0; i < options.length; i += 1) {
        if (options[i].value === value) return i;
      }
      return -1;
    }

    function syncFromSelect() {
      slider.value = String(Math.max(0, indexOfValue(select.value)));
    }

    function canSelect(value) {
      var available = Array.prototype.some.call(select.options, function (option) {
        return option.value === value;
      });
      return available && (!guard || guard.allowed(select, value));
    }

    slider.addEventListener('input', function () {
      var target = parseInt(slider.value, 10);
      var current = indexOfValue(select.value);
      if (isNaN(target) || !options[target] || target === current) return;

      var targetBlocked = !canSelect(options[target].value);
      var towardCurrent = target > current ? -1 : 1;
      // Sin medida actual en la lista solo se prueba el destino.
      var stop = current < 0 ? target + towardCurrent : current;
      for (var i = target; i !== stop; i += towardCurrent) {
        if (i === target ? !targetBlocked : canSelect(options[i].value)) {
          select.value = options[i].value;
          select.dispatchEvent(new Event('change', { bubbles: true }));
          break;
        }
      }
      syncFromSelect();
      if (targetBlocked && guard && guard.blocked) guard.blocked();
    });

    select.addEventListener('change', syncFromSelect);
    if (guard && guard.syncs) guard.syncs.push(syncFromSelect);
    syncFromSelect();
  }

  // Comprueba que elegir `value` en `select` no deja oculta ninguna medida ya
  // elegida segun las visibility_rules del metafield.
  // Aviso breve bajo la vista previa cuando un deslizador llega a una medida
  // bloqueada; el texto sale del metafield (size_block_note).
  function showSizeBlockNote(form, config) {
    var stage = form.querySelector('[data-preview-stage]');
    if (!stage || !stage.parentNode) return;
    var note = form.querySelector('[data-preview-block-note]');
    if (!note) {
      note = document.createElement('p');
      note.className = 'cfg-measure-preview__note';
      note.setAttribute('data-preview-block-note', '');
      note.setAttribute('role', 'status');
      stage.parentNode.insertBefore(note, stage.nextSibling);
    }
    note.innerHTML =
      '<svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" stroke-width="1.8" aria-hidden="true">' +
        '<circle cx="12" cy="12" r="9.5"/><path d="M12 7.5v5.5M12 16.2v.3" stroke-linecap="round"/>' +
      '</svg><span></span>';
    note.querySelector('span').textContent = config.size_block_note || 'Esa combinación de ancho y alto no está disponible.';
    note.hidden = false;
    // vuelve a lanzar la animacion aunque el aviso ya estuviera a la vista
    note.classList.remove('is-shown');
    void note.offsetWidth;
    note.classList.add('is-shown');
    var shape = form.querySelector('[data-preview-shape]');
    if (shape) {
      shape.classList.remove('is-blocked');
      void shape.offsetWidth;
      shape.classList.add('is-blocked');
    }
    clearTimeout(note._hideTimer);
    note._hideTimer = setTimeout(function () { note.hidden = true; }, 9000);
  }

  function measureChangeAllowed(form, config, select, value) {
    if (!config.visibility_rules || !window.DPEvaluator) return true;
    var key = select.getAttribute('data-config-key');
    var state = collectVisibilityState(form, config);
    state[key] = value;
    var vis = window.DPEvaluator.applyVisibility(
      config.visibility_rules, state, config.base_prices, config.intervals
    );
    return sizeStepKeys().every(function (sizeKey) {
      var hidden = vis.hiddenOptions[sizeKey];
      var chosen = state[sizeKey];
      return !hidden || chosen === undefined || !hidden.has(String(chosen));
    });
  }

  function setupPredefinedSizeSliders(widthControl, heightControl, select) {
    var options = getPredefinedSizeOptions(select);
    if (!select || !options.length) {
      if (widthControl) widthControl.hidden = true;
      if (heightControl) heightControl.hidden = true;
      return;
    }

    var widths = uniqueSortedNumbers(options.map(function (option) { return option.width; }));
    var heights = uniqueSortedNumbers(options.map(function (option) { return option.height; }));
    var widthSlider = widthControl && widthControl.querySelector('input[type="range"]');
    var heightSlider = heightControl && heightControl.querySelector('input[type="range"]');
    var widthTicks = widthControl && widthControl.querySelector('[data-preview-width-ticks]');
    var heightTicks = heightControl && heightControl.querySelector('[data-preview-height-ticks]');

    if (!widthSlider || !heightSlider) {
      if (widthControl) widthControl.hidden = true;
      if (heightControl) heightControl.hidden = true;
      return;
    }

    widthControl.hidden = false;
    heightControl.hidden = false;
    widthSlider.min = 0;
    widthSlider.max = String(widths.length - 1);
    widthSlider.step = 1;
    heightSlider.min = 0;
    heightSlider.max = String(heights.length - 1);
    heightSlider.step = 1;

    if (widthTicks) widthTicks.innerHTML = buildNumericTickMarkup(widths, false);
    if (heightTicks) heightTicks.innerHTML = buildNumericTickMarkup(heights, true);

    function currentOption() {
      return options.find(function (option) { return option.index === select.selectedIndex; }) || options[0];
    }

    function syncFromSelect() {
      var option = currentOption();
      widthSlider.value = String(getNearestIndex(widths, option.width));
      heightSlider.value = String(getNearestIndex(heights, option.height));
    }

    function chooseNearest(targetWidth, targetHeight, priority) {
      var exactAxisMatches = options.filter(function (option) {
        return priority === 'width' ? option.width === targetWidth : option.height === targetHeight;
      });
      var pool = exactAxisMatches.length ? exactAxisMatches : options;
      var best = pool[0];
      var bestScore = Infinity;
      pool.forEach(function (option) {
        var widthWeight = priority === 'width' ? 0.55 : 1;
        var heightWeight = priority === 'height' ? 0.55 : 1;
        var score = Math.abs(option.width - targetWidth) * widthWeight + Math.abs(option.height - targetHeight) * heightWeight;
        if (score < bestScore) {
          best = option;
          bestScore = score;
        }
      });
      if (best && select.selectedIndex !== best.index) {
        select.selectedIndex = best.index;
        select.dispatchEvent(new Event('change', { bubbles: true }));
      } else {
        syncFromSelect();
      }
    }

    widthSlider.addEventListener('input', function () {
      var current = currentOption();
      var targetWidth = widths[parseInt(widthSlider.value, 10)] || current.width;
      chooseNearest(targetWidth, current.height, 'width');
    });

    heightSlider.addEventListener('input', function () {
      var current = currentOption();
      var targetHeight = heights[parseInt(heightSlider.value, 10)] || current.height;
      chooseNearest(current.width, targetHeight, 'height');
    });

    select.addEventListener('change', syncFromSelect);
    syncFromSelect();
  }

  // Solo LUXE (config.dependent_sizes): el eje con menos valores manda y el otro
  // enseña solo las medidas que existen con lo elegido.
  function setupDependentSizeSliders(widthControl, heightControl, select) {
    var options = getPredefinedSizeOptions(select);
    if (!select || !options.length) {
      if (widthControl) widthControl.hidden = true;
      if (heightControl) heightControl.hidden = true;
      return;
    }

    var widthSlider = widthControl && widthControl.querySelector('input[type="range"]');
    var heightSlider = heightControl && heightControl.querySelector('input[type="range"]');
    var widthTicks = widthControl && widthControl.querySelector('[data-preview-width-ticks]');
    var heightTicks = heightControl && heightControl.querySelector('[data-preview-height-ticks]');

    if (!widthSlider || !heightSlider) {
      if (widthControl) widthControl.hidden = true;
      if (heightControl) heightControl.hidden = true;
      return;
    }

    var widths = uniqueSortedNumbers(options.map(function (option) { return option.width; }));
    var heights = uniqueSortedNumbers(options.map(function (option) { return option.height; }));

    // Manda el eje con menos valores (en WIN el alto, en ALICE el ancho) y el otro
    // solo ofrece las medidas que existen con lo elegido en el primero. En los
    // redondos (ancho = alto) manda el ancho, porque el alto se oculta. Un eje con
    // un único valor (el alto de 80 de BIDENS) no se enseña: no hay nada que elegir.
    var allSquare = options.every(function (option) { return option.width === option.height; });
    var primaryAxis = allSquare ? 'width' : (heights.length <= widths.length ? 'height' : 'width');
    var secondaryAxis = primaryAxis === 'height' ? 'width' : 'height';
    var axes = {
      width: { slider: widthSlider, ticks: widthTicks, control: widthControl, all: widths, values: widths, reverse: false },
      height: { slider: heightSlider, ticks: heightTicks, control: heightControl, all: heights, values: heights, reverse: true }
    };
    var primary = axes[primaryAxis];
    var secondary = axes[secondaryAxis];

    function axisValue(option, axis) {
      return axis === 'width' ? option.width : option.height;
    }

    function setAxisValues(axis, values) {
      axis.values = values;
      axis.slider.min = 0;
      axis.slider.max = String(Math.max(values.length - 1, 0));
      axis.slider.step = 1;
      axis.slider.disabled = values.length < 2;
      if (axis.ticks) axis.ticks.innerHTML = buildNumericTickMarkup(values, axis.reverse);
      // En los redondos el alto repite el diámetro: no se enseña
      axis.control.hidden = axis.all.length < 2 || (allSquare && axis === secondary);
    }

    function secondaryValuesFor(primaryValue) {
      return uniqueSortedNumbers(options.filter(function (option) {
        return axisValue(option, primaryAxis) === primaryValue;
      }).map(function (option) { return axisValue(option, secondaryAxis); }));
    }

    function currentOption() {
      return options.find(function (option) { return option.index === select.selectedIndex; }) || options[0];
    }

    function selectOption(option) {
      if (option && select.selectedIndex !== option.index) {
        select.selectedIndex = option.index;
        select.dispatchEvent(new Event('change', { bubbles: true }));
      } else {
        syncFromSelect();
      }
    }

    function syncFromSelect() {
      var option = currentOption();
      setAxisValues(secondary, secondaryValuesFor(axisValue(option, primaryAxis)));
      primary.slider.value = String(getNearestIndex(primary.values, axisValue(option, primaryAxis)));
      secondary.slider.value = String(getNearestIndex(secondary.values, axisValue(option, secondaryAxis)));
    }

    primary.slider.addEventListener('input', function () {
      var current = currentOption();
      var target = primary.values[parseInt(primary.slider.value, 10)];
      if (target === undefined) target = axisValue(current, primaryAxis);
      // Con el nuevo valor se queda la medida más parecida a la que había en el otro eje
      var keep = axisValue(current, secondaryAxis);
      var best = null;
      options.forEach(function (option) {
        if (axisValue(option, primaryAxis) !== target) return;
        if (!best || Math.abs(axisValue(option, secondaryAxis) - keep) < Math.abs(axisValue(best, secondaryAxis) - keep)) best = option;
      });
      selectOption(best);
    });

    secondary.slider.addEventListener('input', function () {
      var current = currentOption();
      var target = secondary.values[parseInt(secondary.slider.value, 10)];
      var match = options.find(function (option) {
        return axisValue(option, primaryAxis) === axisValue(current, primaryAxis) && axisValue(option, secondaryAxis) === target;
      });
      selectOption(match || current);
    });

    setAxisValues(primary, primary.all);
    select.addEventListener('change', syncFromSelect);
    syncFromSelect();
  }

  function setMeasureControlLabel(control, label) {
    if (!control) return;
    var labelEl = control.querySelector('.cfg-measure-control__label');
    if (labelEl) labelEl.textContent = label;
  }

  function getNumericSelectRange(select) {
    if (!select) return null;
    var values = Array.prototype.slice.call(select.options || []).map(function (option) {
      return parseMeasureNumber(option.value || option.text);
    }).filter(function (value) {
      return value > 0;
    });
    if (!values.length) return null;
    return {
      min: Math.min.apply(null, values),
      max: Math.max.apply(null, values)
    };
  }

  function initMeasureSliders(form, config) {
    var type = config.type;
    var widthSelect = null;
    var heightSelect = null;
    var widthControl = form.querySelector('[data-preview-width-control]');
    var heightControl = form.querySelector('[data-preview-height-control]');

    if (type === 'circular') {
      widthSelect = findMeasureSelect(form, ['diametro']);
      setMeasureControlLabel(widthControl, 'Diámetro (cm)');
      if (heightControl) heightControl.hidden = true;
    } else if (type === 'predefined_sizes') {
      widthSelect = findMeasureSelect(form, ['medida', 'medidas']);
      setMeasureControlLabel(widthControl, 'Ancho (cm)');
      setMeasureControlLabel(heightControl, 'Alto (cm)');
      if (config.dependent_sizes) {
        setupDependentSizeSliders(widthControl, heightControl, widthSelect);
      } else {
        setupPredefinedSizeSliders(widthControl, heightControl, widthSelect);
      }
      if (config.preview_shape === 'circle') {
        setMeasureControlLabel(widthControl, 'Diámetro (cm)');
        if (heightControl) heightControl.hidden = true;
      }
      return;
    } else {
      widthSelect = findMeasureSelect(form, ['ancho', 'ancho1']);
      heightSelect = findMeasureSelect(form, ['alto', 'alto1']);
      setMeasureControlLabel(widthControl, 'Ancho (cm)');
      setMeasureControlLabel(heightControl, 'Alto (cm)');
    }

    var guard = {
      allowed: function (select, value) { return measureChangeAllowed(form, config, select, value); },
      blocked: function () { showSizeBlockNote(form, config); },
      syncs: []
    };
    form._measureSliderSyncs = guard.syncs;
    setupMeasureSlider(widthControl, widthSelect, false, undefined, guard);
    setupMeasureSlider(heightControl, heightSelect, true, undefined, guard);
  }

  // Forma organica (WIN LUXE): superelipse dibujada en SVG a la medida exacta
  // de la vista previa, con la linea interior paralela al borde. Con
  // border-radius en porcentaje las curvas no casaban con la foto.
  function superellipsePath(w, h, inset, n) {
    var a = w / 2 - inset;
    var b = h / 2 - inset;
    var steps = 180;
    var d = '';
    for (var i = 0; i < steps; i += 1) {
      var t = (i / steps) * Math.PI * 2;
      var c = Math.cos(t);
      var s = Math.sin(t);
      var x = w / 2 + a * (c < 0 ? -1 : 1) * Math.pow(Math.abs(c), 2 / n);
      var y = h / 2 + b * (s < 0 ? -1 : 1) * Math.pow(Math.abs(s), 2 / n);
      d += (i ? 'L' : 'M') + x.toFixed(2) + ' ' + y.toFixed(2);
    }
    return d + 'Z';
  }

  function drawOrganicShape(shapeEl, w, h) {
    var svg = shapeEl.querySelector('[data-preview-organic]');
    if (!w || !h) {
      if (svg) svg.parentNode.removeChild(svg);
      return;
    }
    var ns = 'http://www.w3.org/2000/svg';
    if (!svg) {
      svg = document.createElementNS(ns, 'svg');
      svg.setAttribute('data-preview-organic', '');
      svg.setAttribute('aria-hidden', 'true');
      svg.setAttribute('class', 'cfg-measure-preview__organic');
      svg.innerHTML =
        '<defs><linearGradient id="cfgOrganicFill" x1="0" y1="0" x2="0" y2="1">' +
          '<stop offset="0" stop-color="#ffffff"/><stop offset="1" stop-color="#fcfcf9"/>' +
        '</linearGradient></defs>' +
        '<path class="cfg-measure-preview__organic-outer" fill="url(#cfgOrganicFill)" stroke="rgba(111,117,111,0.88)" stroke-width="3"/>' +
        '<path class="cfg-measure-preview__organic-inner" fill="none" stroke="rgba(111,117,111,0.28)" stroke-width="1"/>';
      shapeEl.insertBefore(svg, shapeEl.firstChild);
    }
    svg.setAttribute('viewBox', '0 0 ' + w + ' ' + h);
    svg.setAttribute('width', w);
    svg.setAttribute('height', h);
    svg.querySelector('.cfg-measure-preview__organic-outer').setAttribute('d', superellipsePath(w, h, 1.5, 3.4));
    svg.querySelector('.cfg-measure-preview__organic-inner').setAttribute('d', superellipsePath(w, h, 11, 3.4));
  }

  function updateMeasurementPreview(form, config) {
    // Las reglas de visibilidad pueden cambiar una medida sin lanzar "change".
    (form._measureSliderSyncs || []).forEach(function (sync) { sync(); });

    var preview = form.querySelector('[data-configurator-preview]');
    if (!preview) return;

    var selectionEl = preview.querySelector('[data-preview-selection]');
    var shapeEl = preview.querySelector('[data-preview-shape]');
    var shapeLabelEl = preview.querySelector('[data-preview-shape-label]');
    var widthEl = preview.querySelector('[data-preview-width]');
    var heightEl = preview.querySelector('[data-preview-height]');
    var stageEl = preview.querySelector('[data-preview-stage]');
    if (!selectionEl || !shapeEl || !widthEl || !heightEl) return;

    var selected = getSelectedValues(form);
    var dims = getPreviewDimensions(selected, config);
    var width = dims.width || 0;
    var height = dims.height || 0;
    var diameterSelect = findMeasureSelect(form, ['diametro', 'medida', 'medidas']);

    if (!width || !height) {
      selectionEl.textContent = 'Selecciona una medida';
      widthEl.textContent = '\u2014';
      heightEl.textContent = '\u2014';
      if (shapeLabelEl) shapeLabelEl.textContent = '';
      shapeEl.style.width = '160px';
      shapeEl.style.height = '160px';
      shapeEl.classList.remove('is-circle', 'is-oval', 'is-organic');
      drawOrganicShape(shapeEl, 0, 0);
      if (stageEl) stageEl.classList.remove('is-circle-mode');
      return;
    }

    var maxWidth = 250;
    var maxHeight = 280;
    var scale = Math.min(maxWidth / width, maxHeight / height);
    var renderWidth = Math.max(54, Math.round(width * scale));
    var renderHeight = Math.max(72, Math.round(height * scale));
    if (dims.shape === 'circle') {
      var range = getNumericSelectRange(diameterSelect);
      var minSize = 118;
      var maxSize = 230;
      var ratio = range && range.max > range.min ? (width - range.min) / (range.max - range.min) : 1;
      ratio = Math.max(0, Math.min(1, ratio));
      var circleSize = Math.round(minSize + (maxSize - minSize) * ratio);
      renderWidth = circleSize;
      renderHeight = circleSize;
    }

    selectionEl.textContent = dims.label || (Math.round(width) + ' x ' + Math.round(height) + ' cm');
    widthEl.textContent = dims.controlLabel || Math.round(width) + ' cm';
    heightEl.textContent = Math.round(height) + ' cm';
    if (shapeLabelEl) {
      shapeLabelEl.textContent = dims.shape === 'circle'
        ? '\u00d8 ' + Math.round(width)
        : Math.round(width) + ' x ' + Math.round(height);
    }
    shapeEl.style.width = renderWidth + 'px';
    shapeEl.style.height = renderHeight + 'px';
    shapeEl.classList.toggle('is-circle', dims.shape === 'circle');
    shapeEl.classList.toggle('is-oval', dims.shape === 'oval');
    shapeEl.classList.toggle('is-organic', dims.shape === 'organic');
    drawOrganicShape(shapeEl, dims.shape === 'organic' ? renderWidth : 0, renderHeight);
    if (stageEl) stageEl.classList.toggle('is-circle-mode', dims.shape === 'circle');
  }

  // Returns total in cents (integer) — stored by initConfigurator for cart use
  // Total de la configuracion actual. Con `overrides` ({clave: valor}) calcula
  // el total que saldria con esas opciones cambiadas, sin tocar el formulario.
  function computeTotal(form, config, basePrice, overrides) {
    var selected       = getSelectedValues(form);
    if (overrides) {
      Object.keys(overrides).forEach(function (k) { selected[k] = overrides[k]; });
    }
    var type           = config.type;
    var priceTable     = config.price_table     || {};
    var transportTable = config.transport_table || {};

    var sizeKey           = '';
    var priceIncrement    = 0;
    var transportIncrement = 0;
    var accessoryTotal    = 0;

    if (type === 'rectangular') {
      var ancho = selected['ancho'] || selected['ancho1'] || '';
      var alto  = selected['alto']  || selected['alto1']  || '';
      if (ancho && alto) sizeKey = ancho + 'x' + alto;
    } else if (type === 'circular') {
      var diam = selected['diametro'] || '';
      if (diam) sizeKey = diam;
    } else if (type === 'predefined_sizes') {
      sizeKey = selected['medida'] || selected['medidas'] || '';
    }

    priceIncrement     = lookupSizeValue(priceTable, sizeKey);
    transportIncrement = lookupSizeValue(transportTable, sizeKey);

    // price_factor = IVA (1.21) × (1 - descuento); increments are stored sin IVA
    var priceFactor = parseFloat(config.price_factor) || 1.0285;

    if (config.equation && window.DPEvaluator) {
      // FASE 3+ path: producto con metafield regenerado a partir de la auditoría
      // Dynamic Product. Replica fórmulas e intervals 1:1.
      var evalState = {};
      Object.keys(selected).forEach(function (k) { evalState[k] = selected[k]; });
      enrichStateWithSize(evalState, config);
      applyTariffBands(evalState, config);
      var evalResult = window.DPEvaluator.evaluateAccessory({
        equation:   config.equation,
        intervals:  config.intervals   || [],
        basePrices: config.base_prices || {},
        state:      evalState,
      });
      accessoryTotal = evalResult.total;
    } else {
      // Legacy path (productos no migrados todavía a equation/intervals).
      var fieldMap = {};
      (config.groups || []).forEach(function (g) {
        (g.fields || []).forEach(function (f) { fieldMap[f.key] = f; });
      });

      var sizeKeys = new Set(['ancho', 'alto', 'ancho1', 'alto1', 'diametro', 'medida', 'medidas']);
      form.querySelectorAll('select[data-config-key]').forEach(function (sel) {
        var key          = sel.getAttribute('data-config-key');
        var affectsPrice = sel.getAttribute('data-affects-price') === 'true';
        if (sizeKeys.has(key)) return;

        var value = selected[key] !== undefined ? selected[key] : sel.value;
        var field = fieldMap[key];

        if (affectsPrice) {
          var selOpt = field && field.options && field.options.find(function (o) {
            return String(o.value) === String(value);
          });
          var priceVal = (selOpt && selOpt.price !== undefined)
            ? parseFloat(selOpt.price)
            : parseFloat(value);
          if (!isNaN(priceVal) && priceVal > 0) accessoryTotal += priceVal;
          return;
        }

        if (field && field.size_price_table && value && value !== '0') {
          var depTable = config[field.size_price_table] || {};
          var depPrice = lookupSizeValue(depTable, sizeKey);
          if (!isNaN(depPrice) && depPrice > 0) accessoryTotal += depPrice;
          return;
        }

        if (field && field.soporte_price_table && value && value !== '0') {
          var sTable   = config[field.soporte_price_table] || {};
          var colorSop = selected['color_soporte'] || '0';
          var sPrice   = parseFloat(((sTable[value] || {})[colorSop]) || 0);
          if (!isNaN(sPrice) && sPrice > 0) accessoryTotal += sPrice;
        }
      });
    }

    var incrementsTotal = (priceIncrement + transportIncrement + accessoryTotal) * priceFactor;
    return basePrice + incrementsTotal;
  }

  function updatePrice(form, config, basePrice, compareAtPrice, fixedDiscountPct) {
    var priceEl = form.querySelector('[data-configurator-price]');
    if (!priceEl) {
      priceEl = document.querySelector('[data-configurator-price]');
    }

    var total = computeTotal(form, config, basePrice, null);

    var priceElOriginal = form.querySelector('[data-configurator-price-original]');
    if (priceElOriginal) {
      priceElOriginal.style.display = 'none';
    }

    priceEl.textContent = formatPrice(total);

    // Sync the native Shopify theme price block (top of product page)
    // El tachado escala en proporcion con el total, para que el descuento que se
    // anuncia (el de la variante base, -15 % o -30 % en Nueva Coleccion) sea el
    // real en cualquier configuracion. Antes se sumaba el incremento tal cual y
    // en un espejo grande el descuento real quedaba muy por debajo del anunciado.
    var displayCompare = 0;
    if (compareAtPrice > basePrice && compareAtPrice > 0) {
      displayCompare = Math.round(total * (compareAtPrice / basePrice) * 100) / 100;
    }
    syncThemePrice(total, displayCompare, fixedDiscountPct);

    return Math.round(total * 100); // cents
  }

  // "Desde" delante del precio hasta que el cliente toca el configurador: el
  // precio inicial es el de la medida mas pequeña sin extras.
  function initFromLabel(form) {
    var targets = [];
    document.querySelectorAll('.f-price').forEach(function (priceEl) {
      if (priceEl.closest('dialog') || priceEl.closest('.product-card')) return;
      if (!priceEl.closest('.product__info-container, sticky-atc-bar')) return;
      var label = document.createElement('span');
      label.className = 'cfg-price-from';
      label.textContent = 'Desde';
      priceEl.insertBefore(label, priceEl.firstChild);
      targets.push(label);
    });
    if (!targets.length) return;

    onFirstInteraction(form, function () {
      targets.forEach(function (label) { label.remove(); });
    });
  }

  // Primera interaccion real del cliente con el configurador: mover un
  // deslizador, elegir una tarjeta o activar un interruptor. No cuentan los
  // eventos que lanza el propio script al sincronizar campos (no son
  // isTrusted), ni el boton de añadir, ni la "i" de informacion.
  // Se escucha en el formulario y no en .configurator-wrapper porque los
  // deslizadores de medida estan fuera de el: al elegir la medida con el
  // deslizador el precio seguia diciendo "Desde".
  // "pointerup" hace falta en pantallas tactiles: assets/theme.js convierte
  // cada toque sobre un boton en un click() sintetico, que no es isTrusted,
  // asi que elegir una tarjeta con el dedo no contaba.
  function onFirstInteraction(form, callback) {
    var events = ['input', 'change', 'click', 'pointerup'];
    function handle(e) {
      if (!e.isTrusted) return;
      var target = e.target && typeof e.target.closest === 'function' ? e.target : null;
      if (!target) return;
      if (target.closest('.configurator-add-btn, .cfg-info-btn')) return;
      if ((e.type === 'click' || e.type === 'pointerup') && !target.closest('button, input, select, label')) return;
      events.forEach(function (name) { form.removeEventListener(name, handle, true); });
      callback();
    }
    events.forEach(function (name) { form.addEventListener(name, handle, true); });
  }

  // ---------------------------------------------------------------------------
  // Barra fija de compra: bloqueada hasta que se configura el espejo
  // ---------------------------------------------------------------------------
  function isConfiguratorTouched(form) {
    return form.dataset.configuratorTouched === 'true';
  }

  // gated = true: el boton de la barra dice "Elegir medida" (texto que ya trae
  // de sections/sticky-atc-bar.liquid) y no añade. gated = false: vuelve a
  // "Añadir al carrito".
  function setStickyGate(form, gated) {
    form.dataset.configuratorTouched = gated ? 'false' : 'true';
    var btn = document.querySelector(STICKY_BUTTON_SELECTOR);
    var text = btn ? btn.querySelector('span') : null;
    if (!text) return;

    var gateLabel = btn.getAttribute('data-cfg-gate-label') || 'Elegir medida';
    if (gated) {
      if (btn.disabled) return; // agotado: se queda con su texto
      if (!btn.getAttribute('data-cfg-add-label')) {
        btn.setAttribute('data-cfg-add-label', text.textContent.trim());
      }
      text.textContent = gateLabel;
    } else {
      var addLabel = btn.getAttribute('data-cfg-add-label');
      if (addLabel) text.textContent = addLabel;
    }
  }

  function scrollToConfigurator(form) {
    var preview = form.querySelector('.cfg-measure-preview');
    var target = preview && preview.offsetParent !== null ? preview : form;
    var offset = 16;
    var header = document.querySelector('.header-section');
    if (header) {
      var position = window.getComputedStyle(header).position;
      if (position === 'sticky' || position === 'fixed') offset += header.offsetHeight;
    }
    var top = target.getBoundingClientRect().top + (window.scrollY || window.pageYOffset) - offset;
    var reduceMotion = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    window.scrollTo({ top: Math.max(0, top), behavior: reduceMotion ? 'auto' : 'smooth' });
  }

  // ---------------------------------------------------------------------------
  // Resumen "Tu espejo", encima del boton de añadir
  //
  // Un espejo a medida no se puede devolver, asi que antes de añadirlo el
  // cliente repasa lo elegido: medida, tono de luz, extras, plazo y total.
  // Las lineas salen de buildProperties() y se agrupan igual que en el carrito
  // (snippets/cart-line-info.liquid), para que diga lo mismo que dira despues.
  // Solo lee: no toca el formulario, ni el precio, ni lo que se envia.
  // ---------------------------------------------------------------------------
  function measureFieldKeys() {
    return ['ancho', 'alto', 'ancho1', 'alto1', 'diametro', 'medida', 'medidas'];
  }

  // Devuelve la funcion que repinta el resumen (con un pequeño retardo, para
  // que arrastrar un deslizador no lo anuncie doce veces), o null si no hay
  // boton de añadir delante del que ponerlo.
  function initSummary(form, config, getTotalCents) {
    var addBtn = form.querySelector('.configurator-add-btn');
    if (!addBtn || form.querySelector('[data-cfg-summary]')) return null;

    var box = document.createElement('section');
    box.className = 'cfg-summary';
    box.setAttribute('data-cfg-summary', '');
    box.setAttribute('aria-labelledby', 'cfg-summary-title');
    box.innerHTML =
      '<p class="cfg-summary__title" id="cfg-summary-title">Tu espejo</p>' +
      '<div class="cfg-summary__body" aria-live="polite">' +
        '<dl class="cfg-summary__list" data-cfg-summary-list></dl>' +
        '<p class="cfg-summary__total">' +
          '<span class="cfg-summary__total-label">Total</span> ' +
          '<strong class="cfg-summary__total-value" data-cfg-summary-total></strong>' +
        '</p>' +
      '</div>';
    addBtn.parentNode.insertBefore(box, addBtn);

    var timer = null;
    function render() {
      try { renderSummary(form, config, box, getTotalCents()); } catch (e) {}
    }
    function schedule() {
      clearTimeout(timer);
      timer = setTimeout(render, 120);
    }

    // La medida solo cuenta como elegida cuando el cliente mueve de verdad su
    // deslizador (o su desplegable): elegir un extra no es elegir medida. Los
    // eventos que lanza el propio script al sincronizar campos no son isTrusted.
    ['input', 'change', 'pointerup'].forEach(function (name) {
      form.addEventListener(name, function (e) {
        if (e.isTrusted && form.dataset.cfgMeasureTouched !== 'true' && isMeasureControl(e.target)) {
          form.dataset.cfgMeasureTouched = 'true';
        }
        // El codigo RAL se escribe en un campo de texto que no lanza "change".
        schedule();
      }, true);
    });

    render();
    return schedule;
  }

  function isMeasureControl(target) {
    if (!target || typeof target.closest !== 'function') return false;
    if (target.closest('[data-configurator-preview] input[type="range"]')) return true;
    var select = target.closest('select[data-config-key]');
    return !!select && measureFieldKeys().indexOf(select.getAttribute('data-config-key')) !== -1;
  }

  // true si la medida que hay puesta es la mas pequeña que ofrece la ficha.
  function isSmallestMeasure(form) {
    var smallest = true;
    var found = false;
    measureFieldKeys().forEach(function (key) {
      var select = form.querySelector('select[data-config-key="' + key + '"]');
      if (!select || !select.options.length) return;
      found = true;
      var sizes = [];
      var current = null;
      if (key === 'medida' || key === 'medidas') {
        getPredefinedSizeOptions(select).forEach(function (option) {
          var area = option.width * option.height;
          sizes.push(area);
          if (option.index === select.selectedIndex) current = area;
        });
      }
      if (!sizes.length) {
        Array.prototype.forEach.call(select.options, function (option, index) {
          var size = parseMeasureNumber(option.value || option.text);
          if (!(size > 0)) return;
          sizes.push(size);
          if (index === select.selectedIndex) current = size;
        });
      }
      if (current === null || sizes.some(function (size) { return size < current; })) smallest = false;
    });
    return found && smallest;
  }

  // Mismas reglas que snippets/cart-line-info.liquid: ancho y alto (o el
  // diametro) van juntos en "Medidas", lo que vale "Sí" se junta en "Extras",
  // y no salen ni "Precio configurado" ni las propiedades internas ("_...").
  function buildSummaryRows(form, config, properties) {
    var measureLabels = [];
    var lightLabels = [];
    (config.groups || []).forEach(function (group) {
      (group.fields || []).forEach(function (field) {
        var label = String(field.label || '').trim();
        if (measureFieldKeys().indexOf(field.key) !== -1) measureLabels.push(label);
        if (/^tipo_luz/.test(field.key) && field.key !== 'tipo_luz_espejo_aumento') lightLabels.push(label);
      });
    });

    var ancho = '';
    var alto = '';
    var diametro = '';
    var extras = [];
    var measureRows = [];
    var lightRows = [];
    var otherRows = [];

    Object.keys(properties || {}).forEach(function (rawKey) {
      var key = String(rawKey).trim();
      var value = properties[rawKey] === null || properties[rawKey] === undefined ? '' : String(properties[rawKey]).trim();
      if (!value || key.charAt(0) === '_' || key === 'Precio configurado') return;

      var lower = key.toLowerCase();
      var number = value.replace(/cm/g, '').trim();
      if (lower.indexOf('ancho') !== -1 && !ancho) { ancho = number; return; }
      if (lower.indexOf('alto') !== -1 && !alto) { alto = number; return; }
      if ((lower.indexOf('diámetro') !== -1 || lower.indexOf('diametro') !== -1) && !diametro) { diametro = number; return; }
      if (value === 'Sí') { extras.push(key); return; }

      var row = { id: 'p:' + key, label: key.replace(/ en cm/g, ''), value: value };
      if (measureLabels.indexOf(key) !== -1) measureRows.push(row);
      else if (lightLabels.indexOf(key) !== -1) lightRows.push(row);
      else otherRows.push(row);
    });

    var measure = '';
    if (ancho && alto) measure = ancho + ' × ' + alto + ' cm';
    else if (diametro) measure = 'Ø ' + diametro + ' cm';
    else if (ancho) measure = ancho + ' cm de ancho';
    if (measure) measureRows.unshift({ id: 'medidas', label: 'Medidas', value: measure });

    if (measureRows.length && form.dataset.cfgMeasureTouched !== 'true') {
      measureRows[0].hint = isSmallestMeasure(form)
        ? 'Medida mínima · aún no has elegido medida'
        : 'Aún no has elegido medida';
    }

    var rows = measureRows.concat(lightRows, otherRows);
    if (extras.length) rows.push({ id: 'extras', label: 'Extras', value: extras.join(', ') });
    // El plazo va a todo el ancho; su etiqueta queda solo para lectores de pantalla.
    rows.push({ id: 'plazo', label: 'Plazo', value: 'Fabricación en 15-20 días hábiles', wide: true });
    return rows;
  }

  // Repinta fila a fila y solo lo que cambia: asi el lector de pantalla
  // (aria-live) anuncia la linea que ha cambiado y no la lista entera.
  function renderSummary(form, config, box, totalCents) {
    var list = box.querySelector('[data-cfg-summary-list]');
    var totalEl = box.querySelector('[data-cfg-summary-total]');
    if (!list || !totalEl) return;

    var properties = buildProperties(form, config, totalCents);
    var rows = buildSummaryRows(form, config, properties);

    var existing = {};
    Array.prototype.forEach.call(list.children, function (el) {
      existing[el.getAttribute('data-row')] = el;
    });

    var previous = null;
    rows.forEach(function (row) {
      var el = existing[row.id];
      if (el) {
        delete existing[row.id];
      } else {
        el = document.createElement('div');
        el.className = 'cfg-summary__row';
        el.setAttribute('data-row', row.id);
        el.appendChild(document.createElement('dt'));
        el.appendChild(document.createElement('dd'));
      }

      var signature = row.label + '\n' + row.value + '\n' + (row.hint || '');
      if (el._cfgSignature !== signature) {
        el._cfgSignature = signature;
        var dt = el.children[0];
        var dd = el.children[1];
        var hint = el.children[2];
        el.classList.toggle('cfg-summary__row--wide', !!row.wide);
        dt.classList.toggle('visually-hidden', !!row.wide);
        if (dt.textContent !== row.label) dt.textContent = row.label;
        if (dd.textContent !== row.value) dd.textContent = row.value;
        if (row.hint) {
          if (!hint) {
            hint = document.createElement('dd');
            hint.className = 'cfg-summary__hint';
            el.appendChild(hint);
          }
          hint.textContent = row.hint;
        } else if (hint) {
          el.removeChild(hint);
        }
      }

      var expected = previous ? previous.nextSibling : list.firstChild;
      if (expected !== el) list.insertBefore(el, expected);
      previous = el;
    });

    Object.keys(existing).forEach(function (id) {
      if (existing[id].parentNode === list) list.removeChild(existing[id]);
    });

    var total = properties['Precio configurado'] || (totalCents > 0 ? formatPrice(totalCents / 100) : '');
    if (totalEl.textContent !== total) totalEl.textContent = total;
    box.hidden = !total;
  }

  function syncThemePrice(total, originalPrice, discountPct) {
    // Modiva uses plain .f-price divs (no custom element).
    // Structure:
    //   .f-price[.f-price--on-sale]
    //     .f-price__regular > .f-price-item--regular   (regular price text)
    //     .f-price__sale    > .f-price-item--sale       (sale price)
    //                       > .f-price-item--regular s  (compare-at, struck through)
    var allPriceEls = document.querySelectorAll('.f-price');
    if (!allPriceEls.length) return;

    allPriceEls.forEach(function (priceEl) {
      if (priceEl.closest('dialog')) return;

      var regularPriceEl = priceEl.querySelector('.f-price__regular .f-price-item--regular');
      var salePriceEl    = priceEl.querySelector('.f-price__sale .f-price-item--sale');
      var comparePriceEl = priceEl.querySelector('.f-price__sale .f-price-item--regular s');

      if (!regularPriceEl && !salePriceEl) return;

      if (originalPrice > 0) {
        priceEl.classList.add('f-price--on-sale');
        if (salePriceEl)    salePriceEl.textContent    = formatPrice(total);
        if (comparePriceEl) comparePriceEl.textContent = formatPrice(originalPrice);
        var badge = priceEl.querySelector('[data-discount-badge]');
        if (badge && discountPct > 0) {
          badge.textContent = '-' + discountPct + '%';
          badge.style.display = '';
        }
      } else {
        priceEl.classList.remove('f-price--on-sale');
        if (regularPriceEl) regularPriceEl.textContent = formatPrice(total);
        var badge = priceEl.querySelector('[data-discount-badge]');
        if (badge) badge.style.display = 'none';
      }
    });
  }

  function formatPrice(amount) {
    // Spanish locale formatting: decimal comma, thousands dot.
    // Se redondea a céntimos con Math.round, igual que la app (variant.mjs): con
    // toFixed(2) un total como 362,035 salía 362,03 € y el carrito cobraba 362,04 €.
    var fixed = (Math.round(amount * 100) / 100).toFixed(2);
    var parts = fixed.split('.');
    var intPart = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, '.');
    return intPart + ',' + parts[1] + '\u20ac';
  }

  function findMatchingEventTarget(event, selector) {
    if (!event) return null;

    var target = event.target;
    if (target && typeof target.closest === 'function') {
      var directMatch = target.closest(selector);
      if (directMatch) return directMatch;
    }

    if (typeof event.composedPath !== 'function') return null;

    var path = event.composedPath();
    for (var i = 0; i < path.length; i += 1) {
      var node = path[i];
      if (node && typeof node.matches === 'function' && node.matches(selector)) {
        return node;
      }
    }

    return null;
  }

  // ---------------------------------------------------------------------------
  // Cart integration
  // ---------------------------------------------------------------------------

  // URL del endpoint vía App Proxy de Shopify.
  // El proxy redirige /apps/configurador/* → servidor Railway.
  var VARIANT_ENDPOINT = '/apps/configurador/api/variant';
  var CART_RETURN_TO = '/cart';

  function handleAddToCart(form, config, totalCents, clickedBtn) {
    // Apply spinner to the button the user actually clicked (top configurator
    // button or sticky-bar button). Fallback to findNativeAddBtn() if no
    // explicit caller, e.g. legacy calls.
    var btn = clickedBtn || findNativeAddBtn();
    var feedback = form.querySelector('.configurator-feedback');
    var productId = form.getAttribute('data-product-id');
    var variantId = form.getAttribute('data-variant-id');

    if (form.dataset.configuratorSubmitting === 'true') {
      return;
    }

    if (!productId || !variantId) {
      showFeedback(feedback, 'error', 'No se pudo identificar el producto. Recarga la página.');
      return;
    }

    if (!totalCents || totalCents <= 0) {
      showFeedback(feedback, 'error', 'Selecciona las medidas para ver el precio antes de añadir al carrito.');
      setButtonLoading(btn, false);
      return;
    }

    var ralValidation = validateRalSelection(form);
    if (!ralValidation.ok) {
      showFeedback(feedback, 'error', ralValidation.message);
      setButtonLoading(btn, false);
      return;
    }

    var selections = getSelectedValues(form);
    var properties = buildProperties(form, config, totalCents);

    form.dataset.configuratorSubmitting = 'true';
    setButtonLoading(btn, true);
    hideFeedback(feedback);

    // Paso 1: pedir al backend la variante shadow con el precio correcto.
    // Con reintentos automáticos para Vercel cold-starts (5xx) y errores de red.
    var VARIANT_MAX_RETRIES = 3;
    var VARIANT_RETRY_DELAYS = [600, 1500, 3000];

    function fetchVariantWithRetry(retriesLeft) {
      return fetch(VARIANT_ENDPOINT, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          productId:  productId,
          variantId:  variantId,
          selections: selections,
          totalCents: totalCents
        })
      })
      .then(function (res) {
        // Retry on 5xx (server / cold start) or 408 (timeout)
        if ((res.status >= 500 || res.status === 408) && retriesLeft > 0) {
          var attempt = VARIANT_MAX_RETRIES - retriesLeft;
          var delay = VARIANT_RETRY_DELAYS[attempt] || 3000;
          console.log('[Configurator] Backend ' + res.status + ', reintentando en ' + delay + 'ms (' + retriesLeft + ' restantes)');
          return new Promise(function (resolve) {
            setTimeout(function () { resolve(fetchVariantWithRetry(retriesLeft - 1)); }, delay);
          });
        }
        if (!res.ok) {
          return res.json().then(function (d) {
            throw new Error(d.error || 'Error al procesar la configuración');
          });
        }
        return res.json();
      }, function (networkErr) {
        // Network error (offline, DNS, etc.) → retry
        if (retriesLeft > 0) {
          var attempt = VARIANT_MAX_RETRIES - retriesLeft;
          var delay = VARIANT_RETRY_DELAYS[attempt] || 3000;
          console.log('[Configurator] Network error, reintentando en ' + delay + 'ms (' + retriesLeft + ' restantes)');
          return new Promise(function (resolve) {
            setTimeout(function () { resolve(fetchVariantWithRetry(retriesLeft - 1)); }, delay);
          });
        }
        throw networkErr;
      });
    }

    fetchVariantWithRetry(VARIANT_MAX_RETRIES)
    .then(function (data) {
      if (!data || !data.variantId) {
        throw new Error('No se pudo preparar la variante configurada.');
      }

      // Paso 2: añadir al carrito via AJAX con reintentos automáticos.
      submitShadowVariantToCart(data.variantId, properties, getDesiredQuantity(), form, btn);
    })
    .catch(function (err) {
      console.error('[Configurator] Error:', err);
      form.dataset.configuratorSubmitting = 'false';
      showFeedback(feedback, 'error', err.message || 'No se pudo añadir al carrito. Inténtalo de nuevo.');
      setButtonLoading(btn, false);
    });
  }

  // Modelo Genexia a partir del SKU de la ficha (gx-<modelo>[-nol][-medida][-color]):
  // gx-kayra -> KAYRA, gx-aran-2-nol-60x120 -> ARÁN 2 NOL, gx-bosco-60-negro -> BOSCO.
  // El SKU va sin tildes; estas son las de los nombres de la tarifa 2026.
  var GENEXIA_TILDES = {
    CAPSULA: 'CÁPSULA', ARAN: 'ARÁN', BAZTAN: 'BAZTÁN', ORION: 'ORIÓN', GARBI: 'GARBÍ',
    INES: 'INÉS', MARTIN: 'MARTÍN', MILAN: 'MILÁN', RODANO: 'RÓDANO', BAMBU: 'BAMBÚ'
  };

  function genexiaModelFromSku(sku) {
    var match = /^gx-(.+)$/i.exec(String(sku || '').trim());
    if (!match) return '';
    var words = [];
    match[1].split('-').some(function (part) {
      if (/^\d{2,}(x\d+)?$/i.test(part)) return true; // medida: detrás solo hay medida o color
      var word = part.toUpperCase();
      words.push(GENEXIA_TILDES[word] || word);
      return false;
    });
    return words.join(' ');
  }

  function buildProperties(form, config, totalCents) {
    var properties = {};
    var sizeKeys = new Set(['ancho', 'alto', 'ancho1', 'alto1', 'diametro', 'medida', 'medidas']);

    // Primera línea del pedido en el admin: el modelo Genexia. El título y el SKU
    // de la línea son los de la variante shadow, con nuestro nombre.
    var genexiaModel = genexiaModelFromSku(form.getAttribute('data-sku'));
    if (genexiaModel) {
      properties['_Modelo Genexia'] = genexiaModel;
    }
    var touchChosen = false;

    // Nota: _precio_cents ya no se incluye — el precio real está en la variante shadow.
    // Tampoco se añade "(+€X)" a la etiqueta de los extras: el `value` de la
    // opción es el importe sin IVA ni factor (o un ID de PrestaShop en los
    // campos condicionados por intervals), así que no coincide con lo que
    // realmente se suma. El precio del extra ya va incluido en el total de la
    // variante shadow.

    // Flatten all fields from config groups for quick lookup
    var fieldMap = {};
    config.groups.forEach(function (group) {
      group.fields.forEach(function (field) {
        fieldMap[field.key] = field;
      });
    });

    // Dropdowns
    form.querySelectorAll('select[data-config-key]').forEach(function (sel) {
      var key = sel.getAttribute('data-config-key');
      var value = sel.value;
      var field = fieldMap[key];
      if (!field) return;

      // Skip fields currently hidden by a visibility condition (their value
      // defaults to the first option, which may not be 'No' — e.g. tipo_luz_espejo_aumento
      // defaults to 'Cálida'). Without this check, hidden-but-not-deactivated
      // fields would leak into cart properties.
      var wrap = sel.closest('.configurator-field');
      if (wrap && wrap.style.display === 'none') return;

      // Resolve selected option preferring selectedIndex (handles duplicate values
      // like tipo_luz where 3 options share value="0"); fall back to find-by-value
      // when the index is shifted by detached <option>s.
      var idx = parseInt(getSelectedOptionIndex(sel), 10);
      var selectedOpt = (idx >= 0 && field.options[idx]) ? field.options[idx] : null;
      if (!selectedOpt || String(selectedOpt.value) !== String(value)) {
        selectedOpt = field.options.find(function (o) { return String(o.value) === String(value); });
      }
      var label = selectedOpt ? selectedOpt.label : value;

      var lowerLabel = label.toLowerCase().trim();
      if (lowerLabel === 'no' || value === '' || value === null) return;

      if (key === 'sensor_on_off' || (key === 'lamina_antivaho' && lowerLabel.indexOf('con sensor') !== -1)) {
        touchChosen = true;
      }

      var propKey = field.label;

      if (sizeKeys.has(key) && !label.toLowerCase().includes('cm')) {
        label = label + ' cm';
      }

      properties[propKey] = label;
    });

    var colorSelect = form.querySelector('select[data-config-key="color"]');
    var ralCode = getRalCode(form);
    if (isRalSelected(colorSelect) && ralCode) {
      properties['Color RAL'] = ralCode;
    }

    // Switches (checkboxes)
    form.querySelectorAll('input[type="checkbox"][data-config-key]').forEach(function (cb) {
      if (!cb.checked) return;
      var key = cb.getAttribute('data-config-key');
      var field = fieldMap[key];
      if (!field) return;
      var wrap = cb.closest('.configurator-field');
      if (wrap && wrap.style.display === 'none') return;
      properties[field.label] = 'Sí';
    });

    // Add total price as a property for order clarity
    var priceEl = form.querySelector('[data-configurator-price]');
    if (priceEl && priceEl.textContent && priceEl.textContent !== '\u2014') {
      properties['Precio configurado'] = priceEl.textContent;
    }

    // Hidden property: original product URL so cart link goes back to the configurator
    var productHandle = form.getAttribute('data-product-handle');
    if (productHandle) {
      properties['_producto_url'] = '/products/' + productHandle;
    }

    // Hidden property: base product SKU for supplier order processing
    var baseSku = form.getAttribute('data-sku');
    if (baseSku) {
      properties['_Referencia'] = baseSku;
    }

    // Hidden property (LUXE con touch): aviso de que el touch se apaga; el texto viene del tema
    var touchNote = form.getAttribute('data-touch-note');
    if (touchNote && touchChosen) {
      properties['_Touch'] = touchNote;
    }

    return properties;
  }

  function getDesiredQuantity() {
    var quantityInput = document.querySelector(
      '.configurator-form input[name="quantity"], ' +
      'product-form-component form input[name="quantity"], ' +
      'sticky-add-to-cart input[name="quantity"]'
    );

    var quantity = quantityInput ? parseInt(quantityInput.value, 10) : 1;
    return !isNaN(quantity) && quantity > 0 ? quantity : 1;
  }

  function submitShadowVariantToCart(variantId, properties, quantity, form, clickedBtn) {
    // Reduced retries to avoid triggering Shopify's /cart/add.js rate limit
    // (which locks the IP for 30 min). 2 retries with longer delays is enough
    // for typical CDN propagation lag without flooding the endpoint.
    var MAX_RETRIES = 2;
    var RETRY_DELAY = 2500;

    var payload = {
      items: [{
        id:         Number(variantId),
        quantity:   quantity || 1,
        properties: properties || {}
      }]
    };

    function attempt(retriesLeft) {
      fetch('/cart/add.js', {
        method:  'POST',
        headers: { 'Content-Type': 'application/json' },
        body:    JSON.stringify(payload)
      })
      .then(function (res) {
        return res.json().then(function (data) {
          return { ok: res.ok, status: res.status, data: data };
        });
      })
      .then(function (result) {
        if (result.ok) {
          window.location.href = CART_RETURN_TO || '/cart';
          return;
        }

        var bodyStr = JSON.stringify(result.data).toLowerCase();

        // 429 = rate limit. Retrying makes it WORSE. Show clear message and stop.
        if (result.status === 429 || bodyStr.includes('30 minut') || bodyStr.includes('intenta de nuevo')) {
          throw new Error('Demasiadas peticiones. Por favor espera unos minutos antes de volver a intentarlo.');
        }

        // Retry: out-of-stock (CDN propagation), variant not found (recién creada),
        // 422/404/5xx en general — suelen ser transitorios al añadir variante shadow
        var isTransient =
          (result.status === 422 || result.status === 404 || result.status >= 500) &&
          (bodyStr.includes('agotado') || bodyStr.includes('sold out') ||
           bodyStr.includes('unavailable') || bodyStr.includes('no se encontr') ||
           bodyStr.includes('not found') || bodyStr.includes('does not exist') ||
           bodyStr.includes('invalid'));

        if (isTransient && retriesLeft > 0) {
          console.log('[Configurator] Error transitorio (' + result.status + '), reintentando... (' + retriesLeft + ' restantes)');
          setTimeout(function () { attempt(retriesLeft - 1); }, RETRY_DELAY);
          return;
        }

        var msg = result.data.description || result.data.message || 'No se pudo añadir al carrito.';
        throw new Error(msg);
      })
      .catch(function (err) {
        if (form) {
          form.dataset.configuratorSubmitting = 'false';
          var btn      = clickedBtn || findNativeAddBtn();
          var feedback = form.querySelector('.configurator-feedback');
          setButtonLoading(btn, false);
          showFeedback(feedback, 'error', err.message || 'No se pudo añadir al carrito. Inténtalo de nuevo.');
        }
      });
    }

    attempt(MAX_RETRIES);
  }

  function appendHiddenInput(form, name, value) {
    var input = document.createElement('input');
    input.type = 'hidden';
    input.name = name;
    input.value = value == null ? '' : String(value);
    form.appendChild(input);
  }

  // ---------------------------------------------------------------------------
  // UI helpers
  // ---------------------------------------------------------------------------
  // Returns Canyon's native add-to-cart button (outside our configurator form)
  function findNativeAddBtn() {
    var candidates = document.querySelectorAll('button[name="add"]');
    for (var i = 0; i < candidates.length; i++) {
      var btn = candidates[i];
      // Skip any button that might be inside our own configurator form
      if (btn.closest('.configurator-form')) continue;
      return btn;
    }
    return null;
  }

  function setButtonLoading(btn, loading) {
    if (!btn) return;
    if (loading) {
      btn.disabled = true;
      btn.dataset.originalHtml = btn.innerHTML;
      // Inject spinner into Canyon's text span if present, else replace innerHTML
      var textSpan = btn.querySelector('.add-to-cart-text__content');
      if (textSpan) {
        textSpan.dataset.originalHtml = textSpan.innerHTML;
        textSpan.innerHTML = '<span class="btn-spinner"></span>A\u00f1adiendo\u2026';
      } else {
        btn.innerHTML = '<span class="btn-spinner"></span>A\u00f1adiendo\u2026';
      }
    } else {
      btn.disabled = false;
      var textSpan = btn.querySelector('.add-to-cart-text__content');
      if (textSpan && textSpan.dataset.originalHtml) {
        textSpan.innerHTML = textSpan.dataset.originalHtml;
        delete textSpan.dataset.originalHtml;
      } else if (btn.dataset.originalHtml) {
        btn.innerHTML = btn.dataset.originalHtml;
      }
      delete btn.dataset.originalHtml;
    }
  }

  function showFeedback(el, type, message) {
    if (!el) return;
    el.className = 'configurator-feedback configurator-feedback--' + type;
    el.textContent = message;
    el.style.display = 'block';
  }

  function hideFeedback(el) {
    if (!el) return;
    el.style.display = 'none';
  }

  function renderRichText(value, className) {
    if (value === null || value === undefined || value === '') return '';
    var content = String(value);
    var attrs = className ? ' class="' + escapeHtml(className) + '"' : '';

    if (/<[a-z][\s\S]*>/i.test(content)) {
      return '<div' + attrs + '>' + content + '</div>';
    }

    return '<p' + attrs + '>' + escapeHtml(content).replace(/\n/g, '<br>') + '</p>';
  }

  function escapeHtml(str) {
    if (str === null || str === undefined) return '';
    return String(str)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#039;');
  }

})();

